import React, { useCallback, useEffect, useState, Fragment, useRef } from "react";
import { useAppSelector, useAppDispatch } from "hooks/redux";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleG, faEllipsisV, faUsdCircle } from "@fortawesome/pro-solid-svg-icons";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import useModal from "hooks/modals/useModal";
import moment from "moment";
import axios, { CancelToken, CancelTokenSource } from "axios";
import { chunk, cloneDeep, groupBy, range, take } from "lodash";
import { IconName } from "@fortawesome/fontawesome-svg-core";

import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import {
  GetPrintCartSigns,
  ExportShotgunTemplate,
  GetTeeTimePricing,
  GetTeeTime,
  GetBookingCategory,
  DeleteTeeTime,
  ImportDrawsheet,
  PostTeeTimeSqueeze,
  GetTeeTimeCartsAvailable,
  IGetTeeTimeCartsAvailableResponse,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import {
  PutCancelBooking,
  PutCancelParticipant,
  PutBookingParticipantNoShow,
  GetBooking,
  PutMoveBooking,
  PutCheckAllParticipant,
  PutBookingConfirmation,
  PutBookingParticipant,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { PostTeeTimeLock } from "api/rpc/facilityAdmin/teesheet/teetime";
import { GetShotGunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/template";
import { RemoveShotgunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";
import { IAPIResponse, StatusCode } from "api/protocols";

import { selectTeeTimeSuccess, selectTeeTime } from "redux/actions/teesheet";
import { showError, showSuccess, initiateNewBooking, initiateEditBooking, enqueue, dequeue } from "redux/actions/ui";

import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { convertTime, valueToString, timeToNumber, formatDate, calculateHexColorBrightness } from "helpers/Helpers";

import { TeeSheetCard as Card } from "components/teeSheetCard";
import TeeTimeToolTip, { ITeeTimeToolTipPricing } from "components/teeTimeToolTip/teeTimeToolTip";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";
import { Badge } from "components/badge/Badge";
import { CustomIcon } from "components/customIcon/CustomIcon";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import BookingModalNew from "./modals/BookingModalNew";
import BookingModalEdit from "./modals/BookingModalEdit";
import { Select } from "components/select/index";
import CreateShotgunModal from "./modals/CreateShotgunModal";
import AuditTrailModal from "./modals/AuditTrailModal";
import TeeTimeDetailsModal from "./modals/TeeTimeDetailsModal";
import AddTeeTimeModal from "./modals/AddTeeTimeModal";
import EditStartHoleModal from "./modals/EditStartHoleModal";
import ImportDrawSheetModal from "./modals/ImportDrawSheetModal";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Callout from "components/callout/Callout";

import { IDivision } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import {
  ITeeTimeNew,
  ITeeTimeSlotNew,
  IBookingCategory,
  ITeeTimeBookingCategory,
  IBookingParticipant,
} from "redux/reducers/models/teetime";
import { TShotgunTemplate } from "redux/reducers/models/teesheet";

import { DragDropContext, Droppable, Draggable, DragStart, DropResult } from "react-beautiful-dnd";

import "./newTeeSheetTabs.scss";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProduct } from "redux/reducers/models/product";
import { LocaleCurrency } from "helpers/Locale";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { PostLineItemsToOrder, PostOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { GetCustomerPaymentMethod } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { useStripe } from "@stripe/react-stripe-js";
import {
  loadingTeetimeTabActionsAreEqual,
  loadingTeetimeTabActionsContainsId,
  TLoadingTeetimeTabsActionStatus,
  TLoadingAddBookingsAction,
  TNewTeeSheetLoadingTeetimeTabAction,
  TLoadingMoveBookingsAction,
  TLoadingUpdateBookingAction,
} from "./loadingTeetimeTabActions";
import { TeesheetActionTypes } from "redux/actions/actionTypes";
import { ITeeTimeTabNew } from "./NewTeeSheet";

interface INewTeeSheetTabs {
  teetimeTabs: ITeeTimeTabNew[];
  teetimeBookingCategories: ITeeTimeBookingCategory[][];
  divisions: IDivision[];
  reloadTeeTimeTabs: () => void;
  reloadActionTeeTimeTabs: (
    loadingTeetimeTabAction: TNewTeeSheetLoadingTeetimeTabAction,
    useLoader: boolean,
  ) => Promise<TLoadingTeetimeTabsActionStatus>;
  teeTimeSearch: string;
  //Manually set the tee times in state
  setTeeTimes?: (teeTimes: Array<ITeeTimeNew>) => void;
  loadingTeetimeTabActions: TNewTeeSheetLoadingTeetimeTabAction[];
  setLoadingTeetimeTabActions: React.Dispatch<React.SetStateAction<TNewTeeSheetLoadingTeetimeTabAction[]>>;
}

interface INewTeeSheetTabsState {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
  selectedTeeTime: ITeeTimeTabNew;
  teeSheetLeagues: ILeague[];
  teeSheetTournaments: ITournament[];
}

interface IDragAndDropMoveGroup {
  sourceTeeTime: ITeeTimeDraggable;
  destTeeTime: ITeeTimeDraggable;
}

interface IDragAndDropState {
  teetimeToolTipDisabled: boolean;
  draggingSlotGroup: ITeeTimeSlotNew[];
  moveGroups: IDragAndDropMoveGroup[];
}

type TDraggableSlots = Array<Array<Array<ITeeTimeSlotNew>>>;
type TDraggableSlot = Array<Array<ITeeTimeSlotNew>>;

export interface ITeeTimeDraggable extends Omit<ITeeTimeTabNew, "slots"> {
  slots: TDraggableSlot;
}

interface IMoveTeetimeConfirmationModal {
  teeTimeBookingID: number;
  startTeeTimeID: number;
  destinationTeeTimeID: number;
  displayHoleConversionWarning: boolean;
  draggingBookingHoles: number;
  sendConfirmation: boolean;
}

export default function NewTeeSheetTabs(props: INewTeeSheetTabs) {
  {
    /* CONSTANTS */
  }
  const { facilityStore, teeSheetStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const windowSize = useWindowSize();
  const { t } = useTranslation();
  const history = useHistory();
  const { cancelToken, cancelRequest } = useCancelToken();
  const stripe = useStripe();

  const { divisions, setTeeTimes, loadingTeetimeTabActions, setLoadingTeetimeTabActions } = props;

  const [bookingCategories, setBookingCategories] = useState<IBookingCategory[]>(undefined);
  const [shotgunTemplates, setShotgunTemplates] = useState<TShotgunTemplate[]>(undefined);
  const [state, setState] = useState<INewTeeSheetTabsState>({
    sortedTeeTimes: null,
    groupedTeeTimes: null,
    selectedTeeTime: null,
    teeSheetLeagues: undefined,
    teeSheetTournaments: undefined,
  });

  const [dragAndDropState, setDragAndDropState] = useState<IDragAndDropState>({
    teetimeToolTipDisabled: false,
    draggingSlotGroup: undefined,
    moveGroups: [],
  });

  {
    /* MODALS */
  }
  const { state: newBookingModal, updateModal: updateNewBookingModal } = useModal({
    clearOnClose: true,
    loading: false,
    loadingText: "",
    postingLock: false,
    slot: null,
    cartsAvailable: {
      nineHole: 0,
      eighteenHole: 0,
    },
  });

  const {
    state: noShowModal,
    updateModal: updateNoShowModal,
    closeModal: closeNoShowModal,
  } = useModal({
    clearOnClose: true,
    type: "charge",
    bookingParticipantIds: [],
    slotGroup: null,
    teeTimeIndex: null,
    chargeNoShow: false,
    bookingId: null,
  });

  const {
    state: noShowChargeModal,
    updateModal: updateNoShowChargeModal,
    closeModal: closeNoShowChargeModal,
  } = useModal({
    clearOnClose: true,
    booking: null,
    selectedProduct: null,
    noShowProducts: null,
    paymentMethod: null,
    confirmationPopup: false,
  });

  const {
    state: noCreditCardPopup,
    updateModal: updateNoCreditCardPopup,
    closeModal: closeNoCreditCardPopup,
  } = useModal({
    clearOnClose: true,
  });

  const {
    state: createShotgunModal,
    updateModal: updateCreateShotgunModal,
    closeModal: closeCreateShotgunModal,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const { state: auditTrailModal, updateModal: updateAuditTrailModal } = useModal({
    clearOnClose: true,
    teetime: null,
    bookingId: null,
    type: null,
  });

  const {
    state: teeTimeDetailsModal,
    updateModal: updateTeeTimeDetailsModal,
    closeModal: closeTeeTimeDetailsModal,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: editStartHoleModal,
    updateModal: updateEditStartHoleModal,
    closeModal: closeEditStartHoleModal,
  } = useModal({
    clearOnClose: true,
    teetime: null,
    start_hole: "",
  });

  const {
    state: addTeeTimeModal,
    updateModal: updateAddTeeTimeModal,
    closeModal: closeAddTeeTimeModal,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: addSqueezeTimeModal,
    updateModal: updateAddSqueezeTimeModal,
    closeModal: closeAddSqueezeTimeModal,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const { state: editBookingModal, updateModal: updateEditBookingModal } = useModal({
    clearOnClose: true,
    selectedTeeTime: null,
    selectedBooking: null,
    teeTimeLock: false,
    loading: false,
    loadingText: "",
    postingLock: false,
    cartsAvailable: {
      nineHole: 0,
      eighteenHole: 0,
    },
  });

  const {
    state: fileImportModal,
    updateModal: updateFileImportModal,
    closeModal: closeFileImportModal,
  } = useModal({ shotgun_application_id: null });

  {
    /* POPUPS */
  }
  const {
    state: blockedTeeTimePopup,
    updateModal: updateBlockedTeeTimePopup,
    closeModal: closeBlockedTeeTimePopup,
  } = useModal();

  const { state: cancelBookingPopup, updateModal: updateCancelBookingPopup } = useModal({
    clearOnClose: true,
    teetimeToCancelDate: "",
    teetimeToCancelTime: "",
    teetimeToCancelBookingId: null,
    send_cancellation_email: true,
  });

  const { state: cancelPlayerPopup, updateModal: updateCancelPlayerPopup } = useModal({
    clearOnClose: true,
    bookingParticipantName: "",
    bookingParticipantId: null,
  });

  const {
    state: teeTimeBookedPopup,
    updateModal: updateTeeTimeBookedPopup,
    closeModal: closeTeeTimeBookedPopup,
  } = useModal();

  const {
    state: moveTeeTimeBookingConfirmation,
    closeModal: closeMoveTeeTimeBookingConfirmationPopup,
    updateModal: updateMoveTeeTimeBookingConfirmationPopup,
  } = useModal<IMoveTeetimeConfirmationModal>({
    clearOnClose: true,
    teeTimeBookingID: null,
    startTeeTimeID: null,
    destinationTeeTimeID: null,
    displayHoleConversionWarning: false,
    draggingBookingHoles: null,
    sendConfirmation: false,
  });

  const {
    state: teeTimeLockedPopup,
    updateModal: updateTeeTimeLockedPopup,
    closeModal: closeTeeTimeLockedPopup,
  } = useModal();

  const {
    state: removeTeeTimePopup,
    updateModal: updateRemoveTeeTimePopup,
    closeModal: closeRemoveTeeTimePopup,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: removeShotgunPopup,
    updateModal: updateRemoveShotgunPopup,
    closeModal: closeRemoveShotgunPopup,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: shotgunCheckInParticipants,
    updateModal: updateCheckInAllShotgunParticipants,
    closeModal: closeCheckInAllShotgunParticipants,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: tournamentCheckInParticipants,
    updateModal: updateCheckInAllTournamentParticipants,
    closeModal: closeCheckInAllTournamentParticipants,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const {
    state: leagueCheckInParticipants,
    updateModal: updateCheckInAllLeagueParticipants,
    closeModal: closeCheckInAllLeagueParticipants,
  } = useModal({
    clearOnClose: true,
    teetime: null,
  });

  const teeSheetTabsExitCancelSource = useRef<CancelTokenSource>(axios.CancelToken.source());

  {
    /* USE EFFECTS */
  }

  useEffect(() => {
    teeSheetTabsExitCancelSource.current = axios.CancelToken.source();

    return () => teeSheetTabsExitCancelSource.current.cancel();
  }, []);

  // Load events when tee times are updated to display information about them on the tee sheet
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (props.teetimeTabs?.length > 0) {
      // Display loader
      updateMainState({ teeSheetTournaments: undefined, teeSheetLeagues: undefined });
      const teeTimes = [...props.teetimeTabs];
      // Load tee sheet tournaments and leagues if they exist
      const tournamentIds = teeTimes
        ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].tournament_id)
        ?.map(teetime => {
          if (teetime.slots[0].tournament_id) {
            return teetime.slots[0].tournament_id;
          }
        });
      void loadTeeSheetTournaments([...new Set(tournamentIds)], source.token);

      const leagueIds = teeTimes
        ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].league_id)
        ?.map(teetime => {
          if (teetime.slots[0].league_id) {
            return teetime.slots[0].league_id;
          }
        });
      void loadTeeSheetLeagues([...new Set(leagueIds)], source.token);
    }
    return () => source.cancel();
  }, [props.teetimeTabs]);

  // Group slots together by booking id
  useEffect(() => {
    if (!props.teetimeTabs) {
      return;
    }

    const source = axios.CancelToken.source();

    let deepCloneTeetimes = cloneDeep(props.teetimeTabs).filter(
      (teetime: ITeeTimeTabNew) => teetime.slots[0].blocked_type !== "shotgun_disabled",
    );

    if (props.teeTimeSearch !== "") {
      deepCloneTeetimes = deepCloneTeetimes.filter(
        (teetime: ITeeTimeTabNew) =>
          teetime.slots?.findIndex(slot =>
            `${slot.customer_first_name} ${slot.customer_last_name}`
              .toLocaleLowerCase()
              .includes(props.teeTimeSearch.toLocaleLowerCase()),
          ) >= 0,
      );
    }

    const sortedTeeTimes = deepCloneTeetimes.sort((current, next) => {
      return current?.slots[0]?.start_time.localeCompare(next?.slots[0]?.start_time);
    });

    const clonedSortedTeeTimes = cloneDeep(sortedTeeTimes);

    const slotGroups: TDraggableSlots = clonedSortedTeeTimes?.map((teetime, rowIndex) => {
      return Object.values(
        groupBy(clonedSortedTeeTimes[rowIndex].slots, s =>
          s.tee_time_booking_id === null ? s.tee_time_slot_id : s.tee_time_booking_id,
        ),
      );
    });

    //potentially sort the array here instead of TeeSheetTabs
    const groupedTeeTimes: ITeeTimeDraggable[] = clonedSortedTeeTimes
      ?.map(teetime => {
        teetime.slots.splice(0); // removes the contents of slots returns empty array
        return teetime as unknown as ITeeTimeDraggable;
      })
      .map((teetime, rowIndex) => {
        teetime.slots.push(...slotGroups[rowIndex]); // pushes slotGroups to empty array
        return teetime;
      });

    if (groupedTeeTimes) {
      updateMainState({ groupedTeeTimes, sortedTeeTimes });
    }
    return () => source.cancel();
  }, [props.teetimeTabs, props.teeTimeSearch]);

  // Load shotgun information
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!bookingCategories && createShotgunModal.isOpen) {
      void loadBookingCategories(source.token);
    }
    if (!shotgunTemplates && createShotgunModal.isOpen) {
      void loadShotgunTemplates(source.token);
    }
    return () => source.cancel();
  }, [createShotgunModal.isOpen]);

  // Load booking categories for tee time details if they're not already loaded
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!bookingCategories && teeTimeDetailsModal.isOpen) {
      void loadBookingCategories(source.token);
    }
    return () => source.cancel();
  }, [teeTimeDetailsModal.isOpen]);

  // Scroll current time into view
  const elementScrollRef = useCallback(element => {
    const tr = timeToNumber();
    const offsetTime = props.teetimeTabs
      .filter(teetime => {
        const time = teetime.start_time.replace(":", "");
        if (parseInt(time) <= tr) {
          return true;
        }
        return false;
      })
      .filter((teetime, i, teetimes) => {
        if (i === teetimes.length - 8 /*magic number, 1 hour is approximately 8 indexes*/) {
          return true;
        }
        return false;
      });

    if (formatDate(teeSheetStore.selectedDate as Date) === formatDate(new Date())) {
      if (offsetTime && element?.id === String(offsetTime[0]?.slots[0]?.tee_time_id)) {
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return () => {
      setState({
        sortedTeeTimes: null,
        groupedTeeTimes: null,
        selectedTeeTime: null,
        teeSheetLeagues: undefined,
        teeSheetTournaments: undefined,
      });
    };
  }, []);

  // FUNCTIONS
  function updateMainState(newMainState: Partial<INewTeeSheetTabsState>) {
    setState((cur: INewTeeSheetTabsState) => {
      return { ...cur, ...newMainState };
    });
  }

  async function loadBookingCategories(token?: CancelToken) {
    const bookingCategoryRes = await GetBookingCategory(null, false, token);

    if (token && token.reason) {
      return;
    }

    if (bookingCategoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.138")));
      return;
    }

    setBookingCategories(bookingCategoryRes.data);
  }

  async function loadShotgunTemplates(token?: CancelToken) {
    const templateRes = await GetShotGunTemplate(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.139")));
    }

    setShotgunTemplates(templateRes.data);
  }

  function findCustomerTypeBackground(slot: ITeeTimeSlotNew) {
    let color = "#FFFFFF";

    if (slot.check_in_status === "checked" && !slot.turn) {
      color = "#82d195";
    } else if (slot.turn) {
      color = "#e7e7e7";
    } else if (slot.no_show_type == "weather") {
      color = "#fcbf49";
    } else if (slot.no_show_type) {
      color = "#d62828";
    } else if (slot.customer_id) {
      color = slot.customer_type_color;
    }

    return color;
  }

  async function handleMouseEnter(
    teeTime: ITeeTimeTabNew,
    token?: CancelToken,
  ): Promise<ITeeTimeToolTipPricing | undefined> {
    const pricingRes = await GetTeeTimePricing({ tee_time_id: teeTime.slots[0].tee_time_id }, false, token);

    if ((token && token.reason) || typeof pricingRes.data === "string") {
      return undefined;
    }

    if (pricingRes.status !== StatusCode.OK) {
      dispatch(showError(pricingRes.message));
      return {
        booking_fee: undefined,
        green_fees: [],
        booking_categories: [],
      };
    }

    return pricingRes.data as ITeeTimeToolTipPricing;
  }

  function checkForCheckedInPlayer(slotGroup: ITeeTimeSlotNew[]) {
    const checkPlayers = slotGroup?.filter(slot => slot.check_in_status !== "unchecked");
    if (checkPlayers.length > 0) {
      return true;
    }
    return false;
  }

  // TEE TIME FUNCTIONS
  async function removeTeeTime() {
    const params = {
      tee_time_id: removeTeeTimePopup.teetime.slots[0].tee_time_id,
    };

    const removeRes = await DeleteTeeTime(params, true);

    if (removeRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.140")));
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.141")));

    closeRemoveTeeTimePopup();

    props.reloadTeeTimeTabs();
  }

  // async function setBlockedType(teetime: any, type: string) {
  //   teetime.blocked_type = type;

  //   const teeTimeRes = await PutTeeTime({ tee_times: [teetime] }, true);

  //   if (teeTimeRes.status !== StatusCode.OK) {
  //     dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.062")));
  //     return;
  //   }
  // }

  async function createSqueezeTime() {
    const teeTimeRes = await PostTeeTimeSqueeze(
      { tee_time_id: addSqueezeTimeModal.teetime?.slots[0]?.tee_time_id },
      true,
    );

    if (teeTimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.142")));
      return;
    }

    props.reloadTeeTimeTabs();
    closeAddSqueezeTimeModal();
  }

  // BOOKING FUNCTIONS
  async function handleInitiateBooking(teetime: ITeeTimeTabNew, slot?: ITeeTimeSlotNew) {
    // Show booking modal
    dispatch(initiateNewBooking());

    // If tee time is not locked then load it
    updateNewBookingModal({
      loading: true,
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.143"),
      slot: slot !== undefined ? slot : null,
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
    });

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [teetimeRes, cartsAvailableRes] = await Promise.all([
      GetTeeTime(
        {
          id: teetime.slots[0].tee_time_id,
          extended: true,
          "extended-bookings": true,
          locks: true,
          turn_tee_time: true,
        },
        false,
      ),
      cartsAvailablePromise,
    ]);

    if (teetimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.218")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartsAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.219")));
        return;
      }

      cartsAvailable9 = cartsAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartsAvailableRes.data?.carts_available_18 ?? 0;
    }

    const updatedTeeTime = teetimeRes.data[0];
    dispatch(selectTeeTimeSuccess(updatedTeeTime));

    if (updatedTeeTime.quantity_remaining === 0) {
      updateTeeTimeBookedPopup({ isOpen: true });
      return;
    }

    // Lock the tee time
    updateNewBookingModal({
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.144"),
      postingLock: true,
    });

    const postLockRes = await PostTeeTimeLock({ tee_time_id: updatedTeeTime.id }, false);

    // Tee time is locked by another user
    if (postLockRes.status !== StatusCode.OK) {
      updateTeeTimeLockedPopup({ isOpen: true });
      updateNewBookingModal({ loading: false, loadingText: "", postingLock: false });
      return;
    }

    updateMainState({ selectedTeeTime: null });
    updateNewBookingModal({
      loading: false,
      loadingText: "",
      postingLock: false,
      cartsAvailable: {
        nineHole: cartsAvailable9,
        eighteenHole: cartsAvailable18,
      },
    });
  }

  async function handleEditBooking(teetime: ITeeTimeTabNew, bookingId: number) {
    // Show edit booking modal while data is loading
    dispatch(initiateEditBooking());

    // Load booking
    updateEditBookingModal({
      isOpen: true,
      loading: true,
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.145"),
      cartsAvailable: {
        nineHole: 0,
        eighteenHole: 0,
      },
    });

    const bookingRes = await GetBooking({ id: bookingId }, false);

    //Check if selected booking has been cancelled by another user
    if (bookingRes.status !== StatusCode.OK || bookingRes.data[0]?.status === "cancelled") {
      props.reloadTeeTimeTabs();
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.063")));
      return;
    }

    const cartsAvailablePromise: Promise<IAPIResponse<IGetTeeTimeCartsAvailableResponse>> = facilityStore
      .teeSheetSettings?.validate_power_cart_count
      ? GetTeeTimeCartsAvailable(
          {
            tee_time_id: teetime.slots[0].tee_time_id,
          },
          false,
        )
      : Promise.resolve({ status: StatusCode.OK, data: null, message: null });

    const [selectTeeTimeAction, cartAvailableRes] = await Promise.all([
      dispatch(selectTeeTime(teetime.slots[0].tee_time_id, true, false)),
      cartsAvailablePromise,
    ]);

    if (selectTeeTimeAction.type === TeesheetActionTypes.UPDATE_SELECTED_TEETIME_ERROR_FACILITY) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.216")));
      return;
    }

    let cartsAvailable9 = 0;
    let cartsAvailable18 = 0;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      if (cartAvailableRes.status !== StatusCode.OK) {
        dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.217")));
        return;
      }

      cartsAvailable9 = cartAvailableRes.data?.carts_available_9 ?? 0;
      cartsAvailable18 = cartAvailableRes.data?.carts_available_18 ?? 0;
    }

    updateEditBookingModal({
      loadingText: t("elements.tee_sheet.tee_sheet_tabs.146"),
      postingLock: true,
    });

    const postLockRes = await PostTeeTimeLock({ tee_time_id: teetime.slots[0].tee_time_id }, false);

    if (postLockRes.status !== StatusCode.OK) {
      return;
    }

    // Tee time is locked by another user
    if (postLockRes.status !== StatusCode.OK) {
      updateEditBookingModal({
        selectedBooking: null,
        selectedTeeTime: null,
        loading: false,
        postingLock: false,
        teeTimeLock: false,
      });
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.005")));
    }

    updateEditBookingModal({
      selectedBooking: bookingRes.data[0],
      selectedTeeTime: teetime,
      cartsAvailable: {
        nineHole: cartsAvailable9,
        eighteenHole: cartsAvailable18,
      },
      loading: false,
      postingLock: false,
    });
  }

  async function cancelBooking() {
    const cancelRes = await PutCancelBooking(
      {
        tee_time_booking_id: Number(cancelBookingPopup.teetimeToCancelBookingId),
        send_cancellation_email: cancelBookingPopup.send_cancellation_email,
      },
      true,
    );

    if (cancelRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.059")));
      return;
    }

    updateCancelBookingPopup({
      isOpen: false,
      teetimeToCancelDate: "",
      teetimeToCancelTime: "",
      teetimeToCancelBookingId: null,
    });

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.060")));
    props.reloadTeeTimeTabs();
  }

  async function cancelBookingParticipant() {
    const cancelRes = await PutCancelParticipant({ id: cancelPlayerPopup.bookingParticipantId }, true);
    if (cancelRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.061")));
      return;
    }

    updateCancelPlayerPopup({
      isOpen: false,
      bookingParticipantName: "",
      bookingParticipantId: null,
    });

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.125")));
    props.reloadTeeTimeTabs();
  }

  async function handleNoShow(chargeNoShow: boolean) {
    if (!noShowModal.bookingParticipantIds || noShowModal?.bookingParticipantIds?.length === 0) {
      return;
    }

    dispatch(enqueue());
    const putBookingParticipantRes = await Promise.all(
      noShowModal?.bookingParticipantIds?.map(participantId =>
        PutBookingParticipantNoShow({ id: participantId, no_show_type: noShowModal?.type }, false),
      ),
    );
    dispatch(dequeue());

    if (putBookingParticipantRes?.some(res => res?.status !== StatusCode.OK)) {
      const participantError = putBookingParticipantRes?.find(res => res?.status !== StatusCode.OK);
      dispatch(
        showError(
          typeof participantError?.data === "string"
            ? participantError?.data
            : t("elements.tee_sheet.tee_sheet_tabs.064") + valueToString(noShowModal.type),
        ),
      );
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.065") + valueToString(noShowModal.type)));

    updateNoShowModal({
      isOpen: false,
      type: "charge",
      bookingParticipantIds: [],
      bookingId: null,
      chargeNoShow: false,
    });

    let updatedParticipants = putBookingParticipantRes?.map(res => res?.data);

    //Charge booking participants
    if (chargeNoShow) {
      const updatedParticipantsCharge = await handleNoShowCharge();
      if (updatedParticipantsCharge) {
        updatedParticipants = updatedParticipantsCharge;
      }
    }

    //Manually update the participants' no show values
    void updateParticipants(updatedParticipants);
  }

  function updateParticipants(updatedParticipants: Array<IBookingParticipant>) {
    const updatedTeeTimes = [...props.teetimeTabs];

    //Use the selected tee time index to update the participants
    if (noShowModal?.teeTimeIndex !== undefined) {
      updatedParticipants?.forEach(participant => {
        updatedTeeTimes.forEach(teeTime => {
          const foundParticipantIndex = teeTime?.slots?.findIndex(
            slot => slot.booking_participant_id === participant.id,
          );
          //Manually update the no show values of the matching participant
          if (foundParticipantIndex !== -1) {
            const participantToUpdate = teeTime.slots[foundParticipantIndex];
            teeTime.slots[foundParticipantIndex] = {
              ...participantToUpdate,
              check_in_status: participant?.check_in_status,
              no_show_type: participant?.no_show_type,
              order_financial_status: participant?.order_financial_status,
            };
          }
        });
      });
      setTeeTimes(updatedTeeTimes);
      void closeNoShowModal();
    } else {
      //Index not found, refresh the tee times
      props.reloadTeeTimeTabs();
    }
  }

  async function handleNoShowCharge() {
    const manualCardPaymentOption = facilityStore?.paymentOptions?.find(
      paymentOption => paymentOption.payment_method === "manual_card",
    );

    if (
      !noShowChargeModal?.paymentMethod ||
      !noShowChargeModal?.selectedProduct ||
      noShowModal?.bookingParticipantIds?.length === 0 ||
      !manualCardPaymentOption
    ) {
      console.error("No show selection error");
      return null;
    }

    dispatch(enqueue());
    //Create order
    const postOrderRes = await PostOrder({ customer_id: noShowChargeModal?.booking?.customer_id }, false);
    if (postOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.191")));
      dispatch(dequeue());
      return null;
    }

    //Create line item order
    const postLineItemOrderRes = await PostLineItemsToOrder(
      {
        order_id: postOrderRes.data.id,
        variant_id: noShowChargeModal?.selectedProduct?.id,
        quantity: noShowModal?.bookingParticipantIds?.length,
      },
      false,
    );

    if (postLineItemOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.192")));
      dispatch(dequeue());
      return null;
    }

    //Create transaction
    const postTransactionRes = await PostTransaction(
      {
        kind: "sale",
        amount: postLineItemOrderRes.data?.total_price,
        order_id: postOrderRes.data?.id,
        payment_option_id: manualCardPaymentOption?.id,
      },
      false,
    );

    if (postTransactionRes?.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.193")));
      dispatch(dequeue());
      return null;
    }

    if (
      postTransactionRes.data.client_secret &&
      noShowChargeModal?.paymentMethod?.stripe_payment_method_id &&
      postTransactionRes.data.id &&
      postOrderRes.data.id
    ) {
      //Make the card payment
      const participantRes = await confirmCardPaymentNoShow(
        postTransactionRes.data.client_secret,
        noShowChargeModal?.paymentMethod?.stripe_payment_method_id,
        postTransactionRes.data.id,
        postOrderRes.data.id,
      );
      if (participantRes) {
        const updatedParticipants: Array<IBookingParticipant> = participantRes?.map(
          res => res?.data as IBookingParticipant,
        );
        dispatch(dequeue());
        return updatedParticipants;
      }
    } else {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.194")));
      dispatch(dequeue());
      return null;
    }

    dispatch(dequeue());
    return null;
  }

  // Confirm Stripe payment intent
  async function confirmCardPaymentNoShow(
    clientSecret: string,
    paymentMethodID: string,
    trans_id: number,
    orderId: number,
  ) {
    if (!stripe) {
      console.error("Stripe hook error");
      return null;
    }
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodID,
    });

    if (stripeError) {
      dispatch(showError(stripeError.message));
      return null;
    }

    //Capture the payment
    const captureRes = await PutCapture({ payment_intent_id: paymentIntent.id, transaction_id: trans_id }, false);

    if (captureRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.195")));
      return null;
    }

    //Update no show paid value and add order to the participants
    const participantRes = await Promise.all(
      noShowModal?.bookingParticipantIds?.map(participantId =>
        PutBookingParticipant({ id: participantId, no_show_paid: true, order_id: orderId }, false),
      ),
    );

    if (participantRes?.some(res => res?.status !== StatusCode.OK)) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.196")));
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.197")));
    void closeNoShowChargeModal();
    void closeNoShowModal();
    return participantRes;
  }

  const handleNavigateToBookingDetails = (teetimeId: number, slot: ITeeTimeSlotNew) => {
    if (slot.turn === 1) {
      return;
    }

    history.push(`/admin/teesheet/details/${teetimeId}`);
  };

  function changeSlotBookingInformation(slot: ITeeTimeSlotNew, bookedSlot: ITeeTimeSlotNew = null) {
    // position and tee_time_slot_position can stay the same
    // quantity_remaining will be set at the very end of drag and drop for source and destination
    slot.tee_time_booking_id = bookedSlot?.tee_time_booking_id ?? null;
    slot.tee_time_slot_status = bookedSlot == null ? "available" : bookedSlot?.tee_time_slot_status ?? null;
    slot.tee_time_bookings_holes = bookedSlot?.tee_time_bookings_holes ?? null;
    slot.tee_time_bookings_source = bookedSlot?.tee_time_bookings_source ?? null;
    slot.tee_time_bookings_completed_at = bookedSlot?.tee_time_bookings_completed_at ?? null;
    slot.has_notes = bookedSlot?.has_notes ?? null;
    slot.power_cart_quantity = bookedSlot?.power_cart_quantity ?? null;
    slot.booking_participant_id = bookedSlot?.booking_participant_id ?? null;
    slot.customer_id = bookedSlot?.customer_id ?? null;
    slot.guest = bookedSlot?.guest ?? null;
    slot.tee_time_booking_participant_status = bookedSlot?.tee_time_booking_participant_status ?? null;
    slot.check_in_status = bookedSlot?.check_in_status ?? null;
    slot.turn = bookedSlot?.turn ?? null;
    slot.customer_first_name = bookedSlot?.customer_first_name ?? null;
    slot.customer_last_name = bookedSlot?.customer_last_name ?? null;
    slot.customer_type_title = bookedSlot?.customer_type_title ?? null;
    slot.customer_type_subtitle = bookedSlot?.customer_type_subtitle ?? null;
    slot.customer_type_color = bookedSlot?.customer_type_color ?? null;
    slot.order_financial_status = bookedSlot?.order_financial_status ?? null;
    slot.no_show_type = bookedSlot?.no_show_type ?? null;
    slot.bag_number = bookedSlot?.bag_number ?? null;
  }

  function handleOnDragStart(initial: DragStart) {
    const draggingSlotGroup = state.groupedTeeTimes[Number(initial.source.droppableId)]?.slots?.find(slotGroup => {
      return slotGroup?.[0]?.tee_time_slot_id === Number(initial.draggableId);
    });

    setDragAndDropState(prev => ({
      ...prev,
      teetimeToolTipDisabled: true,
      draggingSlotGroup: cloneDeep(draggingSlotGroup),
    }));
  }

  function handleOnDragEnd(result: DropResult) {
    setDragAndDropState(prev => ({ ...prev, teetimeToolTipDisabled: false }));

    if (
      !result.destination ||
      result.source.droppableId === result.destination.droppableId ||
      dragAndDropState.draggingSlotGroup == null ||
      dragAndDropState.draggingSlotGroup.some(
        slot =>
          slot.tee_time_booking_id == null || slot.blocked_type === "crossover" || slot.check_in_status === "checked",
      ) ||
      loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, dragAndDropState.draggingSlotGroup[0].tee_time_id)
    ) {
      return;
    }

    const sourceRowId = Number(result.source.droppableId);
    const destRowId = Number(result.destination.droppableId);

    const updatedGroupedTeeTimes = cloneDeep(state.groupedTeeTimes);

    const sourceTeeTime = updatedGroupedTeeTimes[sourceRowId];
    const destTeeTime = updatedGroupedTeeTimes[destRowId];

    if (loadingTeetimeTabActionsContainsId(loadingTeetimeTabActions, destTeeTime.slots[0][0].tee_time_id)) {
      return;
    }

    let draggingToCrossover = false;

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.blocked_type === "crossover") {
          draggingToCrossover = true;
        }
      });
    });

    if (draggingToCrossover) {
      return;
    }

    let moveBookingLength = 0;

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id) {
          moveBookingLength++;
        }
      });
    });

    const freeSlots: ITeeTimeSlotNew[] = [];

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "available") {
          freeSlots.push(slot);
        }
      });
    });

    if (moveBookingLength > freeSlots.length) {
      return;
    }

    const moveSlots = take(freeSlots, moveBookingLength);

    let destDraggingSlotToCopyIndex = 0;

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (moveSlots.some(moveSlot => moveSlot.tee_time_slot_id === slot.tee_time_slot_id)) {
          changeSlotBookingInformation(slot, dragAndDropState.draggingSlotGroup?.[destDraggingSlotToCopyIndex]);
          destDraggingSlotToCopyIndex++;
        }
      });
    });

    const updatedDestSlots: TDraggableSlot = [];

    destTeeTime.slots.forEach(slotGroup => {
      if (
        slotGroup.length === 1 &&
        slotGroup[0].tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id &&
        updatedDestSlots?.[updatedDestSlots.length - 1]?.[0]?.tee_time_booking_id ===
          dragAndDropState.draggingSlotGroup[0].tee_time_booking_id
      ) {
        updatedDestSlots[updatedDestSlots.length - 1].push(slotGroup[0]);
      } else {
        updatedDestSlots.push(slotGroup);
      }
    });

    destTeeTime.slots = updatedDestSlots;

    const updatedSourceSlots: TDraggableSlot = [];
    const shiftIndexes: number[] = [];

    sourceTeeTime.slots.forEach(slotGroup => {
      if (shiftIndexes.length > 0) {
        if (slotGroup.length === 1 && slotGroup[0].tee_time_slot_status === "available") {
          updatedSourceSlots.push(slotGroup);
          shiftIndexes.push(updatedSourceSlots.length - 1);
        } else if (slotGroup.every(slot => slot.tee_time_slot_status === "booked")) {
          const updatedSlotGroup = cloneDeep(slotGroup);

          const originalUpdateSlotGroup = cloneDeep(updatedSlotGroup);
          const originalSlotGroupLength = updatedSlotGroup.length;

          updatedSlotGroup.forEach(slot => {
            changeSlotBookingInformation(slot);
          });

          const availableSlots = chunk(updatedSlotGroup, 1);

          updatedSourceSlots.push(...availableSlots);
          shiftIndexes.push(...range(updatedSourceSlots.length - availableSlots.length, updatedSourceSlots.length));

          const slotIndexesToGroupAndBook = take(shiftIndexes, originalSlotGroupLength);

          const newSlotGroup: ITeeTimeSlotNew[] = [];

          let sourceDraggingSlotToCopyIndex = 0;

          for (const slotIndex of slotIndexesToGroupAndBook) {
            const slot = cloneDeep(updatedSourceSlots[slotIndex][0]);
            changeSlotBookingInformation(slot, originalUpdateSlotGroup?.[sourceDraggingSlotToCopyIndex]);

            newSlotGroup.push(slot);
            sourceDraggingSlotToCopyIndex++;
          }

          updatedSourceSlots.splice(slotIndexesToGroupAndBook[0], slotIndexesToGroupAndBook.length, newSlotGroup);

          shiftIndexes.splice(0, originalSlotGroupLength);
        }
      } else if (
        slotGroup.some(slot => slot.tee_time_booking_id === dragAndDropState.draggingSlotGroup[0].tee_time_booking_id)
      ) {
        const updatedSlotGroup = cloneDeep(slotGroup);

        updatedSlotGroup.forEach(slot => {
          changeSlotBookingInformation(slot);
        });

        const availableSlots = chunk(updatedSlotGroup, 1);

        updatedSourceSlots.push(...availableSlots);
        shiftIndexes.push(...range(updatedSourceSlots.length - availableSlots.length, updatedSourceSlots.length));
      } else {
        updatedSourceSlots.push(slotGroup);
      }
    });

    sourceTeeTime.slots = updatedSourceSlots;

    let sourceQuantityRemaining = 4;
    let destQuantityRemaining = 4;

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "booked") {
          sourceQuantityRemaining--;
        }
      });
    });

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        if (slot.tee_time_slot_status === "booked") {
          destQuantityRemaining--;
        }
      });
    });

    sourceTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        slot.quantity_remaining = sourceQuantityRemaining;
      });
    });

    destTeeTime.slots.forEach(slotGroup => {
      slotGroup.forEach(slot => {
        slot.quantity_remaining = destQuantityRemaining;
      });
    });

    const firstDestSlot = destTeeTime.slots[0][0];
    const destDivisions: number[] = JSON.parse(firstDestSlot.divisions_available);
    const draggingBookingHoles: number = dragAndDropState.draggingSlotGroup[0].tee_time_bookings_holes;

    if (!destDivisions.some(destDivision => destDivision === draggingBookingHoles / 9)) {
      dispatch(
        showError(
          `${t("elements.tee_sheet.tee_sheet_tabs.147")} ${draggingBookingHoles} ${t(
            "elements.tee_sheet.tee_sheet_tabs.148",
          )}`,
        ),
      );

      return;
    }

    let nineHoleAvailable = false;
    let eighteenHoleAvailable = false;

    destDivisions?.forEach(division => {
      if (division === 1) {
        nineHoleAvailable = true;
      } else if (division === 2) {
        const leagueId = firstDestSlot.league_id;
        const tournamentId = firstDestSlot.tournament_id;

        if (
          (destDivisions?.length > 1 && firstDestSlot.turn_tee_time_id == null) ||
          firstDestSlot?.turn_tee_time_blocked_type === "blocked" ||
          ((firstDestSlot?.turn_tee_time_league_id || firstDestSlot?.turn_tee_time_tournament_id) &&
            (leagueId !== firstDestSlot?.turn_tee_time_league_id ||
              tournamentId !== firstDestSlot?.turn_tee_time_tournament_id))
        ) {
          nineHoleAvailable = true;
        } else if (firstDestSlot.turn_tee_time_id != null) {
          if (firstDestSlot?.turn_tee_time_quantity_remaining >= moveBookingLength) {
            eighteenHoleAvailable = true;
          } else {
            nineHoleAvailable = true;
          }
        }
      }
    });

    let displayHoleConversionWarning = false;

    if (!nineHoleAvailable && !eighteenHoleAvailable) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.149")));
      return;
    } else if (
      (draggingBookingHoles === 9 && !nineHoleAvailable) ||
      (draggingBookingHoles === 18 && !eighteenHoleAvailable)
    ) {
      displayHoleConversionWarning = true;
    }

    updateMoveTeeTimeBookingConfirmationPopup({
      isOpen: true,
      teeTimeBookingID: dragAndDropState.draggingSlotGroup[0].tee_time_booking_id,
      startTeeTimeID: dragAndDropState.draggingSlotGroup[0].tee_time_id,
      destinationTeeTimeID: firstDestSlot.tee_time_id,
      displayHoleConversionWarning,
      draggingBookingHoles,
    });

    setDragAndDropState(prev => ({
      ...prev,
      draggingSlotGroup: undefined,
      moveGroups: [...prev.moveGroups, { sourceTeeTime, destTeeTime }],
    }));
  }

  async function startMoveBookingProcess() {
    const startId = moveTeeTimeBookingConfirmation.startTeeTimeID;
    const destId = moveTeeTimeBookingConfirmation.destinationTeeTimeID;
    const bookingId = moveTeeTimeBookingConfirmation.teeTimeBookingID;

    closeMoveTeeTimeBookingConfirmationPopup();

    await moveBooking(
      {
        type: "move-bookings",
        ids: [startId, destId],
      },
      bookingId,
    );
  }

  function handleCancelTeeTimeMove() {
    const startId = moveTeeTimeBookingConfirmation.startTeeTimeID;
    const destId = moveTeeTimeBookingConfirmation.destinationTeeTimeID;

    setDragAndDropState(prev => {
      return {
        ...prev,
        moveGroups: prev.moveGroups.filter(
          moveGroup =>
            moveGroup.sourceTeeTime?.slots?.[0]?.[0]?.tee_time_id !== startId &&
            moveGroup.destTeeTime?.slots?.[0]?.[0]?.tee_time_id !== destId,
        ),
      };
    });

    closeMoveTeeTimeBookingConfirmationPopup();
  }

  async function moveBooking(moveLoadingTeetimeTabAction: TLoadingMoveBookingsAction, bookingId: number) {
    try {
      setLoadingTeetimeTabActions(prev => {
        return [...prev, moveLoadingTeetimeTabAction];
      });

      const putMove = await PutMoveBooking(
        {
          booking_id: bookingId,
          tee_time_id: moveLoadingTeetimeTabAction.ids[1],
        },
        false,
        teeSheetTabsExitCancelSource.current.token,
      );

      if (teeSheetTabsExitCancelSource.current.token?.reason) {
        return;
      }

      if (putMove.status !== StatusCode.OK) {
        dispatch(
          showError(typeof putMove.data === "string" ? putMove.data : t("elements.tee_sheet.tee_sheet_tabs.150")),
        );

        return;
      }

      const loadTeeTimeTabsStatus = await props.reloadActionTeeTimeTabs(moveLoadingTeetimeTabAction, false);

      if (loadTeeTimeTabsStatus === "failure") {
        return;
      }

      if (moveTeeTimeBookingConfirmation.sendConfirmation) {
        const putBookingConfirmationResponse = await PutBookingConfirmation(
          { booking_group_id: putMove.data.booking_group_id },
          false,
          teeSheetTabsExitCancelSource.current.token,
        );

        if (teeSheetTabsExitCancelSource.current.token?.reason) {
          return;
        }

        if (putBookingConfirmationResponse.status !== StatusCode.OK) {
          dispatch(showError(t("elements.tee_sheet.booking_modal_edit.007")));
          return;
        }
      }

      dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.220")));
    } finally {
      setLoadingTeetimeTabActions(prev =>
        prev.filter(
          prevLoadingTeetimeTabAction =>
            !loadingTeetimeTabActionsAreEqual(prevLoadingTeetimeTabAction, moveLoadingTeetimeTabAction),
        ),
      );

      setDragAndDropState(prev => {
        return {
          ...prev,
          moveGroups: prev.moveGroups.filter(
            moveGroup =>
              moveGroup.sourceTeeTime?.slots?.[0]?.[0]?.tee_time_id !== moveLoadingTeetimeTabAction.ids[0] &&
              moveGroup.destTeeTime?.slots?.[0]?.[0]?.tee_time_id !== moveLoadingTeetimeTabAction.ids[1],
          ),
        };
      });
    }
  }

  // EVENT FUNCTIONS
  async function loadTeeSheetTournaments(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const tournamentRes = await GetTournament({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetTournaments: tournamentRes.status === StatusCode.OK ? tournamentRes.data : [] });
    } else {
      updateMainState({ teeSheetTournaments: [] });
    }
  }

  async function loadTeeSheetLeagues(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const leagueRes = await GetLeague({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetLeagues: leagueRes.status == StatusCode.OK ? leagueRes.data : [] });
    } else {
      updateMainState({ teeSheetLeagues: [] });
    }
  }

  async function printCartSigns(date: string, shotgun_application_id: number) {
    const cartSignsRes = await GetPrintCartSigns(
      {
        date,
        shotgun_application_id,
      },
      true,
    );

    if (cartSignsRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.056")));
      return;
    }

    window.open().document.write(cartSignsRes.data);
  }

  async function handleExportShotgunTemplate(teeTime?: ITeeTimeTabNew) {
    const params = {
      shotgun_application_id: teeTime.slots[0].shotgun_application_id,
    };

    const exportRes = await ExportShotgunTemplate(params, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.151")));
      return;
    }

    const templateExport = document.createElement("a");
    templateExport.href = "data:text/csv;charset=utf-8," + encodeURI(exportRes.data);
    templateExport.target = "_blank";
    templateExport.download = `${t("elements.tee_sheet.tee_sheet_tabs.152")}_${moment(new Date())
      .format("YYYY-MM-DD")
      .toString()}`
      .replace(/ /g, "_")
      .toLowerCase()
      .concat(".csv");
    templateExport.click();
  }

  async function removeShotgunTemplate() {
    const params = {
      tee_sheet_id: removeShotgunPopup.teetime.slots[0].tee_sheet_id,
      tournament_id: removeShotgunPopup.teetime.slots[0].tournament_id
        ? removeShotgunPopup.teetime.slots[0].tournament_id
        : null,
      league_id: removeShotgunPopup.teetime.slots[0].league_id ? removeShotgunPopup.teetime.slots[0].league_id : null,
      shotgun_application_id: removeShotgunPopup.teetime.slots[0].shotgun_application_id,
    };

    const removeRes = await RemoveShotgunTemplate(params, true);

    if (removeRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.057")));
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.058")));

    closeRemoveShotgunPopup();

    props.reloadTeeTimeTabs();
  }

  async function importDrawsheet(selectedFile: File, customer_id: number) {
    if (!customer_id) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.153")));
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("shotgun_application_id", fileImportModal.shotgun_application_id);
    formData.append("customer_id", String(customer_id));

    const importRes = await ImportDrawsheet(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.154")));
      closeFileImportModal();
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.155")));
    closeFileImportModal();
    props.reloadTeeTimeTabs();
  }

  function navigateToEventAccount(type: "league" | "tournament", eventId: number) {
    const event =
      type === "league"
        ? state.teeSheetLeagues.find(league => league.id === eventId)
        : state.teeSheetTournaments.find(tournament => tournament.id === eventId);

    history.push(`/admin/settings/accounts/overview/${event.account_id}`);
  }

  function tournamentName(id: number) {
    const tournament = state.teeSheetTournaments?.find(tournament => tournament.id === id);

    return tournament !== undefined ? tournament.name : t("elements.tee_sheet.tee_sheet_tabs.026");
  }

  function leagueName(id: number) {
    const league = state.teeSheetLeagues?.find(league => league.id === id);

    return league !== undefined ? league.name : t("elements.tee_sheet.tee_sheet_tabs.025");
  }

  function tournamentNote(id: number) {
    const tournament = state.teeSheetTournaments?.find(tournament => tournament.id === id);

    return tournament !== undefined ? tournament?.tee_sheet_note : "";
  }

  function handleCheckboxChange(event: any) {
    const { id, checked } = event.target;
    updateCancelBookingPopup({
      [id]: checked,
    });
  }

  function handleNoShowCheckboxChange(participantId: number) {
    let updatedSelectedParticipantIds: Array<number> = [...noShowModal?.bookingParticipantIds];
    const participantIndex = updatedSelectedParticipantIds.indexOf(participantId);
    if (participantIndex === -1) {
      updatedSelectedParticipantIds = [...updatedSelectedParticipantIds, participantId];
    } else {
      updatedSelectedParticipantIds = updatedSelectedParticipantIds?.filter(id => id !== participantId);
    }
    updateNoShowModal({ bookingParticipantIds: updatedSelectedParticipantIds });
  }

  async function getNoShowBooking() {
    const noShowCancelToken = cancelToken();
    //Show loader
    updateNoShowChargeModal({ isOpen: true, booking: undefined });
    if (!noShowModal?.bookingId) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.198")));
      void closeNoShowChargeModal();
      return;
    }

    const bookingRes = await GetBooking({ id: noShowModal?.bookingId }, false, noShowCancelToken);
    if (bookingRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      if (noShowCancelToken?.reason) {
        return;
      }

      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.199")));
      return;
    }
    if (bookingRes?.data?.length > 0) {
      const booking = bookingRes?.data[0];
      if (!booking?.customer_payment_method_id) {
        void closeNoShowChargeModal();
        updateNoCreditCardPopup({ isOpen: true });
        return;
      }
      const noShowProducts = await GetProduct({ type: "No Show Green Fee", extended: true }, false, noShowCancelToken);
      if (noShowProducts?.status !== StatusCode.OK) {
        void closeNoShowChargeModal();
        if (noShowCancelToken?.reason) {
          return;
        }
        dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.200")));
        return;
      }
      //Get the customer payment method information
      void getCustomerPaymentMethod(booking?.customer_payment_method_id, booking?.customer_id);
      updateNoShowChargeModal({ isOpen: true, booking: booking, noShowProducts: noShowProducts?.data });
    } else {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.201")));
      void closeNoShowChargeModal();
      return;
    }
  }

  async function getCustomerPaymentMethod(paymentMethodId: number, customerId: number) {
    const paymentMethodRes = await GetCustomerPaymentMethod({ id: paymentMethodId, customer_id: customerId }, false);

    if (paymentMethodRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.202")));
      return;
    }

    updateNoShowChargeModal({ paymentMethod: paymentMethodRes?.data[0] });
  }

  const renderBookingParticipantIcons = (slot: ITeeTimeSlotNew) => {
    return (
      <>
        {slot.check_in_status === "checked" && (
          <FontAwesomeIcon icon={faCheckCircle} className="booking-participant_icon" />
        )}

        {slot.check_in_status !== "checked" && !slot.no_show_type && (
          <FontAwesomeIcon icon={["far", "circle"]} className="booking-participant_icon" />
        )}

        {slot.turn === 1 && <FontAwesomeIcon icon={["far", "repeat-alt"]} className="booking-participant_icon" />}

        {slot.order_financial_status === "paid" && (
          <FontAwesomeIcon icon={faUsdCircle} className="booking-participant_icon" />
        )}

        {slot.no_show_type && slot.no_show_type !== "weather" && (
          <FontAwesomeIcon icon={["far", "ban"]} className="booking-participant_icon" />
        )}

        {slot.no_show_type === "weather" && (
          <FontAwesomeIcon icon={["far", "thunderstorm"]} className="booking-participant_icon" />
        )}

        <span className="booking-participant-holes-desktop">{slot.tee_time_bookings_holes}</span>
      </>
    );
  };

  const renderBookingCategoryIcons = (teeTimeId: number) => {
    if (!props.teetimeBookingCategories) {
      return;
    }

    const bookingCategories = props.teetimeBookingCategories[teeTimeId];

    if (!bookingCategories) {
      return;
    }

    return (
      <div className="booking-category-icons">
        {bookingCategories?.map((category: ITeeTimeBookingCategory, index: number) => {
          const categoryLabel = category?.title[0]?.toLocaleLowerCase();
          return (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id={`category-${category.title}`}>{category.title}</Tooltip>}
              key={index}
            >
              <FontAwesomeIcon
                icon={["far", `circle-${categoryLabel}` as IconName]}
                className="booking-category-icon"
              />
            </OverlayTrigger>
          );
        })}
      </div>
    );
  };

  function calculateTeeTimeDivisionCodeColors(backgroundHexColor: string): { backgroundColor: string; color: string } {
    const backgroundColorBrightness = calculateHexColorBrightness(backgroundHexColor);

    if (backgroundColorBrightness) {
      return {
        backgroundColor: backgroundHexColor,
        color: backgroundColorBrightness < 50 ? "white" : "black",
      };
    } else {
      return {
        backgroundColor: "#e9e9e9",
        color: "#999999",
      };
    }
  }

  function getDivisionCode(teeTimeSlot: ITeeTimeSlotNew) {
    const division = divisions?.find((division: IDivision) => division.id === teeTimeSlot?.division_id);
    const turnDivision = divisions?.find((division: IDivision) => division.id === teeTimeSlot?.turn_division_id);

    if (division) {
      if (facilityStore.teeSheetSettings.display_turn_division_code) {
        return (
          division.code +
          (teeTimeSlot.turn_tee_time_id != null && turnDivision?.code != null ? ` - ${turnDivision.code}` : "")
        );
      } else {
        return division.code;
      }
    } else {
      return "";
    }
  }

  function findMatchingGroupedTeeTime(teetime: ITeeTimeTabNew): ITeeTimeDraggable {
    for (const moveGroup of dragAndDropState.moveGroups) {
      if (moveGroup.sourceTeeTime.slots[0][0].tee_time_id === teetime.slots[0].tee_time_id) {
        return moveGroup.sourceTeeTime;
      } else if (moveGroup.destTeeTime.slots[0][0].tee_time_id === teetime.slots[0].tee_time_id) {
        return moveGroup.destTeeTime;
      }
    }

    return state.groupedTeeTimes?.find(
      slotGroup => slotGroup.slots[0][0].tee_time_id === teetime.slots[0].tee_time_id,
    );
  }

  const renderTeeSheetTabs = () => {
    // const sunsetSplitTime: string[] = facilityStore?.weather?.sunset.split(":");

    return (
      <>
        <DragDropContext onDragStart={handleOnDragStart} onDragEnd={handleOnDragEnd}>
          <div className="dragDropContext_container">
            {state.sortedTeeTimes?.map((teetime, teetimeIndex: number, teetimes) => {
              const prevTeetime = teetimes[teetimeIndex - 1];
              const nextTeetime = teetimes[teetimeIndex + 1];

              const teetimeLoading = loadingTeetimeTabActionsContainsId(
                loadingTeetimeTabActions,
                teetime.slots[0].tee_time_id,
              );

              // let turnArrow = false;

              // if (teetime.slots[0].turn_tee_time?.quantity_remaining < teetime.quantity_remaining) {
              //   turnArrow = true;
              // }

              return (
                <Fragment key={teetimeIndex}>
                  {/* LEAGUE/TOURNAMENT/SHOTGUN HEADER */}
                  {/* LEAGUE HEADER */}
                  {teetime.slots[0].league_id > 0 &&
                  (prevTeetime === undefined ||
                    prevTeetime.slots[0].league_id === null ||
                    prevTeetime.slots[0].league_id != teetime.slots[0].league_id) ? (
                    <div className="background-black text-white pl-4 pr-4 py-2 rounded-top">
                      <span>
                        <div className="flex justify-between">
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={["far", "users"]} />
                            {state.teeSheetLeagues === undefined ? (
                              <span style={{ display: "inline-flex" }}>
                                <Spin />
                              </span>
                            ) : (
                              leagueName(teetime.slots[0].league_id)
                            )}
                          </div>
                          <div className="text-black">
                            <NavigationDropdownNew
                              showPlainTextLabel
                              rightAlign
                              label={<FontAwesomeIcon className="th-ellipsis text-white" icon={faEllipsisV} />}
                              sections={[
                                [
                                  {
                                    icon: "print",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.066"),
                                    onClick: () =>
                                      printCartSigns(teetime.slots[0].date, teetime.slots[0].shotgun_application_id),
                                  },
                                  {
                                    icon: "dollar-sign",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.067"),
                                    onClick: () => navigateToEventAccount("league", teetime.slots[0].league_id),
                                    hidden: !state.teeSheetLeagues?.find(
                                      league => league.id === teetime.slots[0].league_id,
                                    )?.account_id,
                                  },
                                  {
                                    icon: "trash",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.068"),
                                    onClick: () => updateRemoveShotgunPopup({ isOpen: true, teetime }),
                                    hidden: teetime.slots[0].shotgun_application_id === null,
                                  },
                                  {
                                    icon: "check",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.168"),
                                    onClick: () =>
                                      updateCheckInAllLeagueParticipants({
                                        isOpen: true,
                                        teetime: teetime.slots[0],
                                      }),
                                  },

                                  // {
                                  //   icon: "file-export",
                                  //   label: "Export Shotgun Template",
                                  //   onClick: () => handleExportShotgunTemplate(teetime),
                                  //   hidden: teetime.blocked_type !== "shotgun",
                                  // },
                                ],
                              ]}
                            />
                          </div>
                        </div>
                      </span>
                    </div>
                  ) : null}

                  {/* TOURNAMENT HEADER */}
                  {teetime.slots[0].tournament_id > 0 &&
                  (prevTeetime === undefined ||
                    prevTeetime.slots[0].tournament_id === null ||
                    prevTeetime.slots[0].tournament_id != teetime.slots[0].tournament_id) ? (
                    <div className="background-black text-white pl-4 pr-4 py-2 rounded-top">
                      <span>
                        <div className="flex justify-between">
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={["far", "trophy"]} />
                            {state?.teeSheetTournaments === undefined ? (
                              <div style={{ display: "inline-flex" }}>
                                <Spin />
                              </div>
                            ) : (
                              tournamentName(teetime.slots[0].tournament_id)
                            )}
                          </div>
                          <div className="text-black">
                            <NavigationDropdownNew
                              showPlainTextLabel
                              rightAlign
                              // type=""
                              label={<FontAwesomeIcon className="th-ellipsis text-white" icon={faEllipsisV} />}
                              sections={[
                                [
                                  {
                                    icon: "print",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.066"),
                                    onClick: () =>
                                      printCartSigns(teetime.slots[0].date, teetime.slots[0].shotgun_application_id),
                                  },
                                  {
                                    icon: "dollar-sign",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.067"),
                                    onClick: () => navigateToEventAccount("tournament", teetime.slots[0].tournament_id),
                                    hidden: !state.teeSheetTournaments?.find(
                                      tournament => tournament.id === teetime.slots[0].tournament_id,
                                    )?.account_id,
                                  },
                                  {
                                    icon: "trash",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.068"),
                                    onClick: () => updateRemoveShotgunPopup({ isOpen: true, teetime }),
                                    hidden: teetime.slots[0].shotgun_application_id === null,
                                  },
                                  {
                                    icon: "file-export",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.156"),
                                    onClick: () => handleExportShotgunTemplate(teetime),
                                    hidden: teetime.slots[0].blocked_type !== "shotgun",
                                  },
                                  {
                                    icon: "file-import",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.157"),
                                    onClick: () =>
                                      updateFileImportModal({
                                        isOpen: true,
                                        shotgun_application_id: teetime.slots[0].shotgun_application_id,
                                      }),
                                    hidden: teetime.slots[0].blocked_type !== "shotgun",
                                  },
                                  {
                                    icon: "check",
                                    label: "Check In Participants",
                                    onClick: () =>
                                      updateCheckInAllTournamentParticipants({
                                        isOpen: true,
                                        teetime: teetime.slots[0],
                                      }),
                                  },
                                ],
                              ]}
                            />
                          </div>
                        </div>
                      </span>
                      <p>
                        {tournamentNote(teetime.slots[0].tournament_id)
                          ? tournamentNote(teetime.slots[0].tournament_id).toLocaleLowerCase()
                          : ""}
                      </p>
                    </div>
                  ) : null}

                  {/* SHOTGUN HEADER */}
                  {teetime.slots[0].blocked_type === "shotgun" &&
                  teetime.slots[0].tournament_id === null &&
                  teetime.slots[0].league_id === null &&
                  (prevTeetime === undefined || prevTeetime.slots[0].blocked_type !== "shotgun") ? (
                    <div className="background-primary text-white pl-4 pr-4 py-2 rounded-top">
                      <span>
                        <div className="flex justify-between">
                          <div>{t("elements.tee_sheet.tee_sheet_tabs.069")}</div>
                          <div className="text-black">
                            <NavigationDropdownNew
                              showPlainTextLabel
                              rightAlign
                              label={<FontAwesomeIcon className="th-ellipsis text-white" icon={faEllipsisV} />}
                              sections={[
                                [
                                  {
                                    icon: "print",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.066"),
                                    onClick: () =>
                                      printCartSigns(teetime.slots[0].date, teetime.slots[0].shotgun_application_id),
                                  },
                                  {
                                    icon: "trash",
                                    label: t("elements.tee_sheet.tee_sheet_tabs.068"),
                                    onClick: () => updateRemoveShotgunPopup({ isOpen: true, teetime }),
                                    disabled: teetime.slots[0].shotgun_application_id === null,
                                  },
                                  {
                                    icon: "check",
                                    label: "Check In Participants",
                                    onClick: () =>
                                      updateCheckInAllShotgunParticipants({
                                        isOpen: true,
                                        teetime: teetime.slots[0],
                                      }),
                                    // hidden: teetime.slots[0].blocked_type !== "shotgun",
                                  },
                                ],
                              ]}
                            />
                          </div>
                        </div>
                      </span>
                    </div>
                  ) : null}

                  {/* TEE TIME CONTAINER */}
                  <div
                    className={classNames("tee-time-new", {
                      "pb-3.5 background-darkgray event-side-border":
                        teetime.slots[0].league_id > 0 || teetime.slots[0].tournament_id > 0,
                      "pb-3.5 background-primary-light event-side-border":
                        teetime.slots[0].league_id === null &&
                        teetime.slots[0].tournament_id === null &&
                        teetime.slots[0].blocked_type === "shotgun",
                      "pt-3.5":
                        (teetime.slots[0].league_id > 0 &&
                          (prevTeetime === undefined ||
                            prevTeetime.slots[0].league_id === null ||
                            prevTeetime.slots[0].league_id != teetime.slots[0].league_id)) ||
                        (teetime.slots[0].tournament_id > 0 &&
                          (prevTeetime === undefined ||
                            prevTeetime.slots[0].tournament_id === null ||
                            prevTeetime.slots[0].tournament_id != teetime.slots[0].tournament_id)) ||
                        (teetime.slots[0].blocked_type === "shotgun" &&
                          (prevTeetime === undefined || prevTeetime.slots[0].blocked_type !== "shotgun")),
                      "rounded-bottom event-bottom-border":
                        (teetime.slots[0].league_id > 0 &&
                          (nextTeetime === undefined ||
                            nextTeetime.slots[0].league_id === null ||
                            nextTeetime.slots[0].league_id != teetime.slots[0].league_id)) ||
                        (teetime.slots[0].tournament_id > 0 &&
                          (nextTeetime === undefined ||
                            nextTeetime.slots[0].tournament_id === null ||
                            nextTeetime.slots[0].tournament_id != teetime.slots[0].tournament_id)) ||
                        (teetime.slots[0].blocked_type === "shotgun" &&
                          (nextTeetime === undefined || nextTeetime.slots[0].blocked_type !== "shotgun")),
                      "my-3.5":
                        windowSize.width > MOBILE_WIDTH &&
                        teetime.slots[0].league_id === null &&
                        teetime.slots[0].tournament_id === null &&
                        teetime.slots[0].blocked_type !== "shotgun" &&
                        prevTeetime !== undefined,
                      "my-1.5":
                        windowSize.width <= MOBILE_WIDTH &&
                        teetime.slots[0].league_id === null &&
                        teetime.slots[0].tournament_id === null &&
                        teetime.slots[0].blocked_type !== "shotgun" &&
                        prevTeetime !== undefined,
                    })}
                    key={teetimeIndex}
                    id={String(teetime.slots[0].tee_time_id)}
                    ref={elementScrollRef}
                  >
                    {/* SUNRISE MARKER */}
                    {((teetimeIndex === 0 && facilityStore.weather?.sunrise < teetime?.start_time) ||
                      (facilityStore.weather?.sunrise >= prevTeetime?.start_time &&
                        facilityStore.weather?.sunrise < teetime?.start_time)) && (
                      <div className="flex items-center mb-3.5">
                        <div style={{ marginLeft: "6.4rem" }} className="sun-time-line"></div>
                        <span className="sun-time-hour">
                          <FontAwesomeIcon icon={["far", "sunrise"]} className="booking-participant_icon mt-1" />
                          {convertTime(facilityStore.weather?.sunrise)}
                        </span>
                        <div style={{ marginRight: "8px" }} className="sun-time-line"></div>
                      </div>
                    )}

                    {/* HOUR MARKERS */}
                    {teetimeIndex === 0 ||
                    convertTime(teetime.start_time).split(":")[0] !==
                      convertTime(prevTeetime.start_time).split(":")[0] ? (
                      <div className="tee-time hour-block">
                        <p className="tee-time time tee-time hour">
                          {convertTime(teetime.start_time).split(":")[0] +
                            ":00 " +
                            convertTime(teetime.start_time).split(" ")[1]}
                        </p>
                        <div className="tee-time hour-line"></div>
                      </div>
                    ) : null}

                    {/* TEE TIME */}
                    <div className="tee-time_container">
                      {/* TEE TIME START TIME */}
                      <button className="tee-time-time cursor-pointer text-left" onClick={() => {}}>
                        <p
                          className={
                            teetime.slots[0].tournament_id || teetime.slots[0].league_id
                              ? "tee-time-time-text-event"
                              : "tee-time-time-text"
                          }
                        >
                          {teetime.slots[0].blocked_type === "shotgun" ||
                          teetime.slots[0].blocked_type === "shotgun_crossover" ? (
                            <span>{teetime.slots[0].start_hole}</span>
                          ) : (
                            <span>{convertTime(teetime.start_time, windowSize.width <= MOBILE_WIDTH)}</span>
                          )}
                        </p>
                      </button>

                      {/* DIVISION CODE */}
                      <p
                        className="tee-time-division-code"
                        style={calculateTeeTimeDivisionCodeColors(
                          divisions?.find((division: IDivision) => division.id === teetime.slots[0].division_id)?.color,
                        )}
                      >
                        {getDivisionCode(teetime.slots[0])}
                      </p>

                      {facilityStore.teeSheetSettings?.validate_power_cart_count && (
                        <OverlayTrigger
                          placement="right"
                          show={teetime.power_cart_numbers?.length > 0 ? undefined : false}
                          overlay={
                            <Tooltip id={`teeTimePowerCartAvailability-${teetimeIndex}`}>
                              {teetime.power_cart_numbers}
                            </Tooltip>
                          }
                        >
                          <div className="tee-time-power-cart-availability">
                            {`${teetime.power_cart_quantity}/${teetime.carts_available}`}
                          </div>
                        </OverlayTrigger>
                      )}

                      {/* SLOTS */}
                      <div className="tee-time-slots">
                        <Droppable
                          key={teetimeIndex.toString()}
                          droppableId={teetimeIndex.toString()}
                          type="LIST"
                          direction="horizontal"
                        >
                          {provided => (
                            <span className="row-container" ref={provided.innerRef} {...provided.droppableProps}>
                              {findMatchingGroupedTeeTime(teetime)?.slots?.map((slotGroup, groupIndex: number) => {
                                const slotId = slotGroup[0]?.tee_time_slot_id.toString();

                                return (
                                  <Draggable
                                    key={slotId}
                                    draggableId={slotId}
                                    isDragDisabled={slotGroup.some(
                                      slot =>
                                        slot.tee_time_booking_id == null ||
                                        slot.blocked_type === "crossover" ||
                                        slot.check_in_status === "checked" ||
                                        teetimeLoading,
                                    )}
                                    index={groupIndex}
                                  >
                                    {provided => (
                                      <div
                                        className={classNames(
                                          "tee-time-slots_container",
                                          `tee-time-slot_spaced_${slotGroup.length}`,
                                          {
                                            "tee-time-slots_last": teetime.slots.length === groupIndex + 1,
                                          },
                                        )}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ref={provided.innerRef}
                                      >
                                        {slotGroup.map(
                                          (slot, slotIndex: number) => {
                                            // BOOKED SLOT
                                            if (slot.customer_id) {
                                              return (
                                                <div
                                                  key={slotIndex}
                                                  className={classNames("tee-time-slot", {
                                                    "tee-time-slot_checked": slot.check_in_status === "checked",
                                                    "tee-time-slot_crossover": slot.blocked_type === "crossover",
                                                    "tee-time-slot_blocked": slot.blocked_type === "blocked",
                                                    "tee-time-slot_no-show": slot.no_show_type !== null,
                                                    "tee-time-slot_turn": slot.turn,
                                                    "tee-time-slot_no-show-weather": slot.no_show_type === "weather",
                                                    "tee-time-slot_booking_engine":
                                                      slot.tee_time_bookings_source === "booking_engine",
                                                    "tee-time-slot_has_notes": slot.has_notes,
                                                    "tee-time-slot_customer-type-display":
                                                      facilityStore?.teeSheetSettings?.display_customer_type,
                                                    "tee-time-slot_loading": teetimeLoading,
                                                  })}
                                                  style={{ backgroundColor: findCustomerTypeBackground(slot) }}
                                                  onClick={() => {
                                                    if (!teetimeLoading) {
                                                      handleNavigateToBookingDetails(slot.tee_time_id, slot);
                                                    }
                                                  }}
                                                >
                                                  <RightClickMenu
                                                    disableAllActions={teetimeLoading}
                                                    informationSection={[
                                                      `${t("elements.tee_sheet.tee_sheet_tabs.070")} ${moment
                                                        .utc(slot.tee_time_bookings_completed_at)
                                                        .local()
                                                        .format("lll")}`,
                                                      ...(slot.no_show_type
                                                        ? [
                                                            <Badge
                                                              key="no_show_badge"
                                                              size="xsmall"
                                                              colour="#FDB022"
                                                              outline
                                                            >
                                                              {slot.no_show_type === "defer"
                                                                ? "Defer Charge"
                                                                : valueToString(slot.no_show_type)}
                                                            </Badge>,
                                                          ]
                                                        : []),
                                                    ]}
                                                    sections={[
                                                      [
                                                        {
                                                          icon: "pencil",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.071"),
                                                          action: () =>
                                                            handleEditBooking(teetime, slot.tee_time_booking_id),
                                                        },
                                                        {
                                                          icon: "ban",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.072"),
                                                          action: () => {
                                                            const teetimeToCancelDate = moment(slot.date).format(
                                                              "MMMM Do, YYYY",
                                                            );
                                                            const teetimeToCancelTime = convertTime(teetime.start_time);

                                                            updateCancelBookingPopup({
                                                              isOpen: true,
                                                              teetimeToCancelDate,
                                                              teetimeToCancelTime,
                                                              teetimeToCancelBookingId: slot.tee_time_booking_id,
                                                            });
                                                          },
                                                          disabled: checkForCheckedInPlayer(slotGroup),
                                                        },
                                                        {
                                                          icon: "user-slash",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.126"),
                                                          action: () => {
                                                            updateCancelPlayerPopup({
                                                              isOpen: true,
                                                              bookingParticipantName: `${slot.customer_first_name} ${slot.customer_last_name}`,
                                                              bookingParticipantId: slot.booking_participant_id,
                                                            });
                                                          },
                                                          disabled: slot.check_in_status !== "unchecked",
                                                        },
                                                        {
                                                          icon: "cancel",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.073"),
                                                          action: () => {
                                                            updateNoShowModal({
                                                              isOpen: true,
                                                              bookingParticipantIds: [slot.booking_participant_id],
                                                              slotGroup: slotGroup,
                                                              bookingId: slot?.tee_time_booking_id,
                                                              teeTimeIndex: teetimeIndex,
                                                            });
                                                          },
                                                          disabled:
                                                            slot.tee_time_booking_participant_status === "no_show",
                                                        },
                                                        {
                                                          icon: "list",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.074"),
                                                          action: () =>
                                                            updateAuditTrailModal({
                                                              isOpen: true,
                                                              teetime,
                                                              type: "booking",
                                                              bookingId: slot.tee_time_booking_id,
                                                            }),
                                                        },
                                                      ],
                                                      [
                                                        ...(slot.shotgun_application_id
                                                          ? [
                                                              {
                                                                icon: "golf-ball-tee" as IconName,
                                                                title: t("elements.tee_sheet.tee_sheet_tabs.169"),
                                                                action: () =>
                                                                  updateEditStartHoleModal({
                                                                    isOpen: true,
                                                                    teetime,
                                                                    start_hole: teetime.slots[0].start_hole,
                                                                  }),
                                                              },
                                                            ]
                                                          : []),
                                                        {
                                                          icon: "circle-info",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.075"),
                                                          action: () =>
                                                            updateTeeTimeDetailsModal({ isOpen: true, teetime }),
                                                        },
                                                        {
                                                          icon: "list",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.076"),
                                                          action: () =>
                                                            updateAuditTrailModal({
                                                              isOpen: true,
                                                              teetime,
                                                              type: "teeTime",
                                                            }),
                                                        },
                                                        {
                                                          icon: "flag",
                                                          title: t("elements.tee_sheet.tee_sheet_tabs.077"),
                                                          action: () =>
                                                            updateCreateShotgunModal({ isOpen: true, teetime }),
                                                        },
                                                        {
                                                          icon: "distribute-spacing-vertical",
                                                          title: slot.shotgun_application_id
                                                            ? t("elements.tee_sheet.tee_sheet_tabs.158")
                                                            : t("elements.tee_sheet.tee_sheet_tabs.159"),
                                                          action: () =>
                                                            slot.shotgun_application_id
                                                              ? updateAddTeeTimeModal({ isOpen: true, teetime })
                                                              : updateAddSqueezeTimeModal({
                                                                  isOpen: true,
                                                                  teetime,
                                                                }),
                                                        },
                                                      ],
                                                    ]}
                                                  >
                                                    <div className="tee-time-slot_content">
                                                      <div className="tee-time-slot-booking-participant">
                                                        <div className="booking-participant_content">
                                                          {windowSize.width > MOBILE_WIDTH &&
                                                            renderBookingParticipantIcons(slot)}

                                                          <div>
                                                            <div className="booking-participant-name">
                                                              <span>
                                                                {`${slot.customer_first_name} ${slot.customer_last_name}`}
                                                                {slot.guest ? (
                                                                  <FontAwesomeIcon
                                                                    style={{ marginLeft: "0.5rem" }}
                                                                    icon={faCircleG}
                                                                    size="1x"
                                                                  />
                                                                ) : null}
                                                                {slot.has_high_priority_notes ? (
                                                                  <FontAwesomeIcon
                                                                    style={{ marginLeft: "0.5rem" }}
                                                                    icon={faExclamationCircle}
                                                                    size="1x"
                                                                    className="text-error"
                                                                  />
                                                                ) : null}
                                                              </span>
                                                            </div>

                                                            {facilityStore?.teeSheetSettings?.display_customer_type &&
                                                            !slot.guest ? (
                                                              <div className="booking-participant-customer-type">
                                                                <span>{slot.customer_type_title}</span>
                                                                {slot.customer_type_subtitle ? (
                                                                  <span>{` - ${slot.customer_type_subtitle}`}</span>
                                                                ) : null}
                                                              </div>
                                                            ) : null}
                                                          </div>
                                                        </div>

                                                        <div className="booking-participant-secondary_content">
                                                          <div className="booking-participant-holes-mobile">
                                                            {slot.tee_time_bookings_holes}
                                                          </div>
                                                          <div className="booking-participant-secondary_content-icons">
                                                            {windowSize.width <= MOBILE_WIDTH &&
                                                              renderBookingParticipantIcons(slot)}

                                                            {slot.tee_time_bookings_source === "booking_engine" && (
                                                              <FontAwesomeIcon
                                                                icon={["far", "earth-americas"]}
                                                                className="booking-participant_icon"
                                                              />
                                                            )}

                                                            {slot.tee_time_bookings_source === "golfnow" && (
                                                              <div className={"golfNow-logo"}>
                                                                <img src="/public/images/gn-logo-small.svg" />
                                                              </div>
                                                            )}

                                                            {slot.has_notes && !slot.guest ? (
                                                              <FontAwesomeIcon
                                                                icon={["far", "sticky-note"]}
                                                                className="booking-participant_icon"
                                                              />
                                                            ) : null}

                                                            {slot.power_cart_quantity > 0 ? (
                                                              <div className="booking-participant_icon">
                                                                <CustomIcon
                                                                  src="../../../public/images/powercart2-01.svg"
                                                                  size="small"
                                                                />
                                                              </div>
                                                            ) : (
                                                              <FontAwesomeIcon
                                                                icon={["far", "person-walking"]}
                                                                className="booking-participant_icon"
                                                              />
                                                            )}

                                                            {slot.bag_number !== null && (
                                                              <CustomIcon
                                                                src="../../../public/images/golf-bag.svg"
                                                                size="small"
                                                              />
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </RightClickMenu>
                                                </div>
                                              );
                                            } else {
                                              // UNBOOKED TEE TIME
                                              return (
                                                <div
                                                  key={slotIndex}
                                                  className={classNames("tee-time-slot", {
                                                    "tee-time-slot_crossover":
                                                      slot.blocked_type === "crossover" ||
                                                      slot.blocked_type === "shotgun_crossover",
                                                    "tee-time-slot_blocked":
                                                      slot.blocked_type === "blocked" ||
                                                      slot.blocked_type === "shotgun_blocked" ||
                                                      slot.blocked_type === "shotgun_disabled",
                                                    "tee-time-slot_no-show":
                                                      slot.no_show_type && slot.no_show_type !== "weather",
                                                    "tee-time-slot_no-show-weather": slot.no_show_type === "weather",
                                                    "tee-time-slot_customer-type-display":
                                                      facilityStore?.teeSheetSettings?.display_customer_type,
                                                    "tee-time-slot_loading": teetimeLoading,
                                                  })}
                                                  onClick={() => {
                                                    if (!teetimeLoading) {
                                                      if (
                                                        teetime.slots[0].blocked_type === "shotgun_blocked" ||
                                                        teetime.slots[0].blocked_type === "blocked"
                                                      ) {
                                                        updateMainState({ selectedTeeTime: teetime });
                                                        updateBlockedTeeTimePopup({ isOpen: true });
                                                      } else {
                                                        void handleInitiateBooking(teetime, slot);
                                                      }
                                                    }
                                                  }}
                                                >
                                                  <TeeTimeToolTip
                                                    prepaidRequired={false}
                                                    cardRequired={false}
                                                    parentId={slot.tee_time_slot_id}
                                                    onMouseEnter={token => handleMouseEnter(teetime, token)}
                                                    disable={dragAndDropState.teetimeToolTipDisabled}
                                                  >
                                                    <RightClickMenu
                                                      disableAllActions={teetimeLoading}
                                                      sections={[
                                                        //
                                                        //   {
                                                        //     icon: "circle",
                                                        //     title: t("elements.tee_sheet.tee_sheet_tabs.079"),
                                                        //     action: () =>  setBlockedType(teetime, "open"),
                                                        //     disabled: slot.blocked_type === "open",
                                                        //   },
                                                        //   {
                                                        //     icon: "ban",
                                                        //     title: t("elements.tee_sheet.tee_sheet_tabs.080"),
                                                        //     action: () => setBlockedType(teetime, "blocked"),
                                                        //     disabled: slot.blocked_type === "blocked",
                                                        //   },
                                                        //   {
                                                        //     icon: "arrows-repeat",
                                                        //     title: t("elements.tee_sheet.tee_sheet_tabs.081"),
                                                        //     action: () => setBlockedType(teetime, "crossover"),
                                                        //     disabled: slot.blocked_type === "crossover",
                                                        //   },
                                                        // ],
                                                        [
                                                          ...(slot.shotgun_application_id
                                                            ? [
                                                                {
                                                                  icon: "golf-ball-tee" as IconName,
                                                                  title: t("elements.tee_sheet.tee_sheet_tabs.169"),
                                                                  action: () =>
                                                                    updateEditStartHoleModal({
                                                                      isOpen: true,
                                                                      teetime,
                                                                      start_hole: teetime.slots[0].start_hole,
                                                                    }),
                                                                },
                                                              ]
                                                            : []),
                                                          {
                                                            icon: "circle-info",
                                                            title: t("elements.tee_sheet.tee_sheet_tabs.082"),
                                                            action: () =>
                                                              updateTeeTimeDetailsModal({
                                                                isOpen: true,
                                                                teetime: teetime,
                                                              }),
                                                          },
                                                          {
                                                            icon: "list",
                                                            title: t("elements.tee_sheet.tee_sheet_tabs.083"),
                                                            action: () =>
                                                              updateAuditTrailModal({
                                                                isOpen: true,
                                                                teetime: teetime,
                                                                type: "teeTime",
                                                              }),
                                                          },
                                                          {
                                                            icon: "flag",
                                                            title: t("elements.tee_sheet.tee_sheet_tabs.084"),
                                                            action: () =>
                                                              updateCreateShotgunModal({ isOpen: true, teetime }),
                                                          },
                                                          {
                                                            icon: "distribute-spacing-vertical",
                                                            title: slot.shotgun_application_id
                                                              ? t("elements.tee_sheet.tee_sheet_tabs.158")
                                                              : t("elements.tee_sheet.tee_sheet_tabs.159"),
                                                            action: () =>
                                                              slot.shotgun_application_id
                                                                ? updateAddTeeTimeModal({ isOpen: true, teetime })
                                                                : updateAddSqueezeTimeModal({
                                                                    isOpen: true,
                                                                    teetime,
                                                                  }),
                                                          },
                                                          {
                                                            icon: "ban",
                                                            title: t("elements.tee_sheet.tee_sheet_tabs.160"),
                                                            action: () =>
                                                              updateRemoveTeeTimePopup({ isOpen: true, teetime }),
                                                            disabled: slot.quantity_remaining !== 4,
                                                          },
                                                        ],
                                                      ]}
                                                    >
                                                      <div className="tee-time-slot_content">
                                                        {slot.blocked_type !== null && slot.blocked_type !== "open" ? (
                                                          <div className="">
                                                            {slot.blocked_type === "blocked" && (
                                                              <p className="text-center">
                                                                <FontAwesomeIcon
                                                                  icon={["far", "ban"]}
                                                                  className="booking-participant_icon"
                                                                />
                                                              </p>
                                                            )}

                                                            {slot.blocked_type === "crossover" && (
                                                              <p className="text-center">
                                                                <FontAwesomeIcon
                                                                  icon={["far", "repeat-alt"]}
                                                                  className="booking-participant_icon"
                                                                />
                                                              </p>
                                                            )}

                                                            {slot.blocked_type === "shotgun" && (
                                                              <p className="text-center">
                                                                <FontAwesomeIcon
                                                                  icon={["far", "plus-circle"]}
                                                                  className="booking-participant_icon"
                                                                />
                                                              </p>
                                                            )}

                                                            {slot.blocked_type === "shotgun_blocked" && (
                                                              <p className="text-center">
                                                                <FontAwesomeIcon
                                                                  icon={["far", "ban"]}
                                                                  className="booking-participant_icon"
                                                                />
                                                              </p>
                                                            )}

                                                            {slot.blocked_type === "shotgun_disabled" && (
                                                              <p className="text-center">
                                                                <em>{t("elements.tee_sheet.tee_sheet_tabs.029")}</em>
                                                              </p>
                                                            )}

                                                            {slot.blocked_type === "shotgun_crossover" && (
                                                              <p className="text-center">
                                                                <FontAwesomeIcon
                                                                  icon={["far", "repeat-alt"]}
                                                                  className="booking-participant_icon"
                                                                />
                                                              </p>
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <div>
                                                            <p className="text-center">
                                                              <FontAwesomeIcon
                                                                icon={["far", "plus-circle"]}
                                                                className="booking-participant_icon"
                                                              />
                                                            </p>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </RightClickMenu>
                                                  </TeeTimeToolTip>
                                                </div>
                                              );
                                            }
                                          }, // slot group map
                                        )}
                                      </div> // tee-time-slots_container
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </span>
                          )}
                        </Droppable>
                      </div>
                      {windowSize.width > MOBILE_WIDTH && renderBookingCategoryIcons(teetime?.slots[0]?.tee_time_id)}
                    </div>

                    {/* SUNSET MARKER */}
                    {((teetimeIndex === props.teetimeTabs?.length - 1 &&
                      facilityStore.weather?.sunset > teetime?.start_time) ||
                      (facilityStore.weather?.sunset <= nextTeetime?.start_time &&
                        facilityStore.weather?.sunset > teetime?.start_time)) && (
                      <div className="flex items-center mb-3.5">
                        <div style={{ marginLeft: "6.4rem" }} className="sun-time-line"></div>
                        <span className="sun-time-hour">
                          <FontAwesomeIcon icon={["far", "sunset"]} className="booking-participant_icon mt-1" />
                          {convertTime(facilityStore.weather?.sunset)}
                        </span>
                        <div style={{ marginRight: "8px" }} className="sun-time-line"></div>
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </DragDropContext>
      </>
    );
  };

  async function checkInAllTournamentParticipants() {
    const tournamentId = tournamentCheckInParticipants.teetime?.tournament_id;
    const startTime = tournamentCheckInParticipants.teetime?.start_time;

    const participantIds: number[] = [];

    state.groupedTeeTimes?.map((teetime, index: number) => {
      if (
        Number(teetime.slots[0][0].tournament_id) === Number(tournamentId) &&
        String(teetime.slots[0][0].start_time) === startTime
      ) {
        for (let i = index; i < state.groupedTeeTimes.length; i++) {
          if (state.groupedTeeTimes[i].slots[0][0].tournament_id !== Number(tournamentId)) {
            return;
          }

          const currentTeeTime = state.groupedTeeTimes[i];

          currentTeeTime.slots?.map(slot => {
            if (slot.length > 1) {
              slot?.map(guestSlot => {
                if (guestSlot.booking_participant_id !== null && String(guestSlot.check_in_status) === "unchecked") {
                  participantIds.push(guestSlot.booking_participant_id);
                }
              });
            } else {
              if (slot[0].booking_participant_id !== null && String(slot[0].check_in_status) === "unchecked") {
                participantIds.push(slot[0].booking_participant_id);
              }
            }
          });
        }
      }
    });

    if (participantIds.length === 0) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.170")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.171")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.172")));
    props.reloadTeeTimeTabs();
  }

  async function checkInAllLeagueParticipants() {
    const leagueId = leagueCheckInParticipants.teetime?.league_id;
    const startTime = leagueCheckInParticipants.teetime?.start_time;
    const participantIds: number[] = [];

    state.groupedTeeTimes?.map((teetime, index: number) => {
      if (
        Number(teetime.slots[0][0].league_id) === Number(leagueId) &&
        String(teetime.slots[0][0].start_time) === startTime
      ) {
        for (let i = index; i < state.groupedTeeTimes.length; i++) {
          if (state.groupedTeeTimes[i].slots[0][0].league_id !== Number(leagueId)) {
            return;
          }

          const currentTeeTime = state.groupedTeeTimes[i];
          currentTeeTime.slots?.map(slot => {
            if (slot.length > 1) {
              slot?.map(guestSlot => {
                if (guestSlot.booking_participant_id !== null && String(guestSlot.check_in_status) === "unchecked") {
                  participantIds.push(guestSlot.booking_participant_id);
                }
              });
            } else {
              if (slot[0].booking_participant_id !== null && String(slot[0].check_in_status) === "unchecked") {
                participantIds.push(slot[0].booking_participant_id);
              }
            }
          });
        }
      }
    });

    if (participantIds.length === 0) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.173")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.174")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.175")));
    props.reloadTeeTimeTabs();
  }

  async function checkInAllShotgunParticipants() {
    const shotgunId = shotgunCheckInParticipants.teetime?.shotgun_application_id;
    const startTime = shotgunCheckInParticipants.teetime?.start_time;

    const participantIds: number[] = [];

    state.groupedTeeTimes?.map(teetime => {
      if (Number(teetime.slots[0][0].shotgun_application_id) === Number(shotgunId)) {
        console.log(teetime);
        teetime.slots?.map(slot => {
          if (slot.length > 1) {
            slot?.map(guestSlot => {
              if (
                guestSlot.booking_participant_id !== null &&
                String(guestSlot.check_in_status) === "unchecked" &&
                String(guestSlot.start_time) === startTime
              ) {
                participantIds.push(guestSlot.booking_participant_id);
              }
            });
          } else {
            if (
              slot[0].booking_participant_id !== null &&
              String(slot[0].check_in_status) === "unchecked" &&
              String(slot[0].start_time) === startTime
            ) {
              participantIds.push(slot[0].booking_participant_id);
            }
          }
        });
      }
    });

    if (participantIds.length === 0) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.176")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    const checkInRes = await PutCheckAllParticipant({ booking_participant_ids: participantIds }, true);

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.tee_sheet_tabs.177")));
      updateCheckInAllShotgunParticipants({
        isOpen: false,
      });
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.tee_sheet_tabs.178")));
    props.reloadTeeTimeTabs();
  }

  return (
    <>
      {state.groupedTeeTimes ? (
        <>
          {state.groupedTeeTimes.length > 0 ? (
            <Card.Group className="tee-sheet-tabs-scroll">{renderTeeSheetTabs()}</Card.Group>
          ) : (
            <Callout type="error" title={t("elements.tee_sheet.tee_sheet_tabs.161")} />
          )}
        </>
      ) : (
        <div style={{ height: "24px" }}>
          <Spin />
        </div>
      )}

      <Portal isMounted={moveTeeTimeBookingConfirmation.isOpen}>
        <Popup
          open={moveTeeTimeBookingConfirmation.isOpen}
          title=""
          description=""
          type="warning"
          onOk={startMoveBookingProcess}
          okText={t("elements.tee_sheet.tee_sheet_tabs.223")}
          cancelText={t("elements.tee_sheet.tee_sheet_tabs.224")}
          onCancel={handleCancelTeeTimeMove}
        >
          <div className="tee-sheet-move-confirmation-heading">{t("elements.tee_sheet.tee_sheet_tabs.221")}</div>
          <div className="tee-sheet-move-confirmation-description">{t("elements.tee_sheet.tee_sheet_tabs.222")}</div>
          <div className="pt-2">
            <Checkbox
              size="small"
              value={moveTeeTimeBookingConfirmation.sendConfirmation}
              checked={moveTeeTimeBookingConfirmation.sendConfirmation}
              onChange={() =>
                updateMoveTeeTimeBookingConfirmationPopup({
                  sendConfirmation: !moveTeeTimeBookingConfirmation.sendConfirmation,
                })
              }
              label={t("elements.tee_sheet.tee_sheet_tabs.225")}
            />
          </div>
          {moveTeeTimeBookingConfirmation.displayHoleConversionWarning && (
            <div className="pt-3">
              <div className="tee-sheet-move-confirmation-heading">{t("elements.tee_sheet.tee_sheet_tabs.163")}</div>
              <div className="tee-sheet-move-confirmation-description">
                {`${t("elements.tee_sheet.tee_sheet_tabs.164")} ${
                  moveTeeTimeBookingConfirmation.draggingBookingHoles
                } ${t("elements.tee_sheet.tee_sheet_tabs.180")} ${
                  moveTeeTimeBookingConfirmation.draggingBookingHoles === 9 ? 18 : 9
                }?`}
              </div>
            </div>
          )}
        </Popup>
      </Portal>
      {/* MODALS */}
      {/* NEW BOOKING MODAL */}
      <BookingModalNew
        cartsAvailable={newBookingModal.cartsAvailable}
        slot={newBookingModal.slot}
        teetimes={props.teetimeTabs}
        loading={newBookingModal.loading}
        loadingText={newBookingModal.loadingText}
        reloadTeeTimes={props.reloadTeeTimeTabs}
        reloadActionTeeTimeTabs={(addTeetimeTabAction: TLoadingAddBookingsAction) =>
          props.reloadActionTeeTimeTabs(addTeetimeTabAction, true)
        }
      />

      {/* NO SHOW MODAL */}
      <Portal isMounted={noShowModal.isOpen}>
        <Popup
          open={noShowModal.isOpen}
          type="info"
          title={t("elements.tee_sheet.tee_sheet_tabs.203")}
          description={t("elements.tee_sheet.tee_sheet_tabs.119")}
          onOk={() => (noShowModal?.chargeNoShow ? getNoShowBooking() : handleNoShow(false))}
          okText={
            noShowModal?.chargeNoShow
              ? t("elements.tee_sheet.tee_sheet_tabs.204")
              : t("elements.tee_sheet.tee_sheet_tabs.120")
          }
          okDisabled={
            !noShowModal.type || !noShowModal.bookingParticipantIds || noShowModal?.bookingParticipantIds?.length === 0
          }
          cancelText={t("elements.tee_sheet.tee_sheet_tabs.121")}
          onCancel={() =>
            updateNoShowModal({
              isOpen: false,
              type: "charge",
              bookingParticipantIds: [],
              bookingId: null,
              teeTimeIndex: null,
            })
          }
        >
          <Select defaultValue={noShowModal.type} onChange={(value: string) => updateNoShowModal({ type: value })}>
            <Select.Option value={"charge"} name={"Charge"}>
              <span>{t("elements.tee_sheet.tee_sheet_tabs.122")}</span>
            </Select.Option>
            <Select.Option value={"defer"} name={"Defer Charge"}>
              <span>{t("elements.tee_sheet.tee_sheet_tabs.123")}</span>
            </Select.Option>
            <Select.Option value={"weather"} name={"Weather"}>
              <span>{t("elements.tee_sheet.tee_sheet_tabs.124")}</span>
            </Select.Option>
          </Select>
          <br />
          {noShowModal?.slotGroup
            ?.filter((filteredSlot: ITeeTimeSlotNew) => filteredSlot?.check_in_status === "unchecked")
            ?.map((slot: ITeeTimeSlotNew, index: number) => {
              return (
                <div
                  key={index}
                  className="tee-time-no-show-row "
                  onClick={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                >
                  <div className="pointer-events-none">
                    <Checkbox
                      size="small"
                      value={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                      checked={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                      onChange={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                      labelHidden
                    />
                  </div>
                  <p>
                    {slot?.customer_first_name} {slot?.customer_last_name} {slot?.guest ? " - Guest" : ""}
                  </p>
                </div>
              );
            })}
          <br />
          <div className="flex justify-right">
            <Checkbox
              size="small"
              value={noShowModal?.chargeNoShow}
              checked={noShowModal?.chargeNoShow}
              onChange={e => updateNoShowModal({ chargeNoShow: e.target.checked })}
              label={t("elements.tee_sheet.tee_sheet_tabs.205")}
            />
          </div>
        </Popup>
      </Portal>

      <Portal isMounted={noCreditCardPopup.isOpen}>
        <Popup
          open={noCreditCardPopup.isOpen}
          type="info"
          title={t("elements.tee_sheet.tee_sheet_tabs.206")}
          description={t("elements.tee_sheet.tee_sheet_tabs.207")}
          onOk={closeNoCreditCardPopup}
          okText={t("elements.tee_sheet.tee_sheet_tabs.208")}
          stacked
        />
      </Portal>

      {/* CHARGE NO SHOW MODAL */}
      <Portal isMounted={noShowChargeModal?.isOpen}>
        <Sheet
          open={noShowChargeModal?.isOpen}
          size="medium"
          closable
          title={t("elements.tee_sheet.tee_sheet_tabs.209")}
          onCancel={() => {
            closeNoShowChargeModal();
            cancelRequest();
          }}
          onOk={() => updateNoShowChargeModal({ confirmationPopup: true })}
          cancelText={t("elements.tee_sheet.tee_sheet_tabs.121")}
          okText={t("elements.tee_sheet.tee_sheet_tabs.210")}
          stacked
          okDisabled={
            !noShowChargeModal?.paymentMethod ||
            !noShowChargeModal?.selectedProduct ||
            noShowModal?.bookingParticipantIds?.length < 1
              ? true
              : false
          }
          overflow
        >
          {noShowChargeModal?.booking === undefined ? (
            <div style={{ display: "flex", margin: "auto", height: "100%" }}>
              <div className="text-center m-auto">
                <div style={{ height: "32px" }}>
                  <Spin />
                </div>
                <div>Loading...</div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {noShowModal?.slotGroup
                ?.filter((filteredSlot: ITeeTimeSlotNew) => filteredSlot?.check_in_status === "unchecked")
                ?.map((slot: ITeeTimeSlotNew, index: number) => {
                  return (
                    <div
                      key={index}
                      className="tee-time-no-show-row "
                      onClick={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                    >
                      <div className="pointer-events-none">
                        <Checkbox
                          size="small"
                          value={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                          checked={noShowModal?.bookingParticipantIds?.includes(slot?.booking_participant_id)}
                          onChange={() => handleNoShowCheckboxChange(slot?.booking_participant_id)}
                          labelHidden
                        />
                      </div>
                      <p>
                        {slot?.customer_first_name} {slot?.customer_last_name} {slot?.guest ? " - Guest" : ""}
                      </p>
                    </div>
                  );
                })}
              <Select
                label={t("secure.facility.tee_sheet.tee_sheet_booking_details.060")}
                onChange={(productId, product) => updateNoShowChargeModal({ selectedProduct: product })}
                defaultValue={noShowChargeModal.selectedProduct?.id}
              >
                {noShowChargeModal?.noShowProducts?.length >= 1 &&
                  noShowChargeModal?.noShowProducts?.map((item: IProduct) => {
                    const productTitle = item?.title;
                    const preferredTitle = item?.preferred_title;
                    return (
                      item.variants &&
                      item.variants
                        .filter(variantFiltered => variantFiltered.price > 0)
                        .map((variant, i) => {
                          return (
                            <Select.Option key={i} value={variant.id} name={variant.title} extraValues={variant}>
                              <div>
                                <span>{preferredTitle ? preferredTitle : productTitle} </span>
                                {productTitle !== variant.title && (
                                  <span className="text-subdued text-sm">{variant.title}</span>
                                )}
                                <span>
                                  {" "}
                                  - <LocaleCurrency currency="cad" amount={variant.price} />
                                </span>
                              </div>
                            </Select.Option>
                          );
                        })
                    );
                  })}
              </Select>

              <div className="mt-auto text-lg">
                <ul>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">{noShowChargeModal?.selectedProduct?.title}</span>
                    <LocaleCurrency currency="cad" amount={noShowChargeModal?.selectedProduct?.price ?? 0} />
                  </li>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">
                      {t("secure.facility.tee_sheet.tee_sheet_booking_details.063")}
                    </span>
                    <span>{noShowModal?.bookingParticipantIds?.length}</span>
                  </li>
                  <li className="flex justify-end text-bold">
                    <span className="mr-4">{t("secure.facility.tee_sheet.tee_sheet_booking_details.064")}</span>
                    <LocaleCurrency
                      currency="cad"
                      amount={
                        noShowChargeModal?.selectedProduct
                          ? noShowModal?.bookingParticipantIds?.length * noShowChargeModal?.selectedProduct?.price
                          : 0
                      }
                    />
                  </li>
                </ul>
              </div>
            </div>
          )}
        </Sheet>
      </Portal>

      {/* NO SHOW CHARGE CONFIRMATION */}
      <Portal isMounted={noShowChargeModal?.confirmationPopup}>
        <Popup
          onCancel={() => updateNoShowChargeModal({ confirmationPopup: false })}
          onOk={() => handleNoShow(true)}
          okText="Charge"
          open={noShowChargeModal?.confirmationPopup}
          type="info"
          title={t("elements.tee_sheet.tee_sheet_tabs.212")}
          stacked
          description={`${t("elements.tee_sheet.tee_sheet_tabs.213")} ${
            noShowChargeModal?.paymentMethod?.last4 as string
          } ${t("elements.tee_sheet.tee_sheet_tabs.214")}`}
        />
      </Portal>

      {/* CREATE SHOTGUN MODAL */}
      <Portal isMounted={createShotgunModal.isOpen}>
        <CreateShotgunModal
          open={createShotgunModal.isOpen}
          teetime={createShotgunModal.teetime}
          divisions={divisions}
          bookingCategories={bookingCategories}
          templates={shotgunTemplates}
          onOk={() => {
            closeCreateShotgunModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeCreateShotgunModal}
        />
      </Portal>

      {/* AUDIT TRAIL MODAL */}
      <Portal isMounted={auditTrailModal.isOpen}>
        <AuditTrailModal
          open={auditTrailModal.isOpen}
          teetime={auditTrailModal.teetime}
          bookingId={auditTrailModal.bookingId}
          onCancel={() =>
            updateAuditTrailModal({
              isOpen: false,
              teetime: null,
              type: null,
            })
          }
          type={auditTrailModal.type}
        />
      </Portal>

      {/* TEE TIME DETAILS MODAL */}
      <Portal isMounted={teeTimeDetailsModal.isOpen}>
        <TeeTimeDetailsModal
          open={teeTimeDetailsModal.isOpen}
          teetime={teeTimeDetailsModal.teetime}
          bookingCategories={bookingCategories}
          onCancel={closeTeeTimeDetailsModal}
        />
      </Portal>

      {/* ADD TEE TIME MODAL */}
      <Portal isMounted={addTeeTimeModal.isOpen}>
        <AddTeeTimeModal
          open={addTeeTimeModal.isOpen}
          teetime={addTeeTimeModal.teetime}
          onOk={() => {
            closeAddTeeTimeModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeAddTeeTimeModal}
        />
      </Portal>

      {/* ADD SQUEEZE TIME POPUP */}
      <Portal isMounted={addSqueezeTimeModal.isOpen}>
        <Popup
          open={addSqueezeTimeModal.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.159")}
          description={t("elements.tee_sheet.tee_sheet_tabs.162")}
          onOk={createSqueezeTime}
          okText={t("elements.tee_sheet.tee_sheet_tabs.167")}
          onCancel={closeAddSqueezeTimeModal}
          backDropCancel={true}
        />
      </Portal>

      {/* EDIT START HOLE MODAL */}
      <Portal isMounted={editStartHoleModal.isOpen}>
        <EditStartHoleModal
          open={editStartHoleModal.isOpen}
          teetime={editStartHoleModal.teetime}
          onOk={() => {
            closeEditStartHoleModal();
            props.reloadTeeTimeTabs();
          }}
          onCancel={closeEditStartHoleModal}
        />
      </Portal>

      {/* EDIT BOOKING MODAL */}
      <Portal isMounted={editBookingModal.isOpen}>
        <BookingModalEdit
          cartsAvailable={editBookingModal.cartsAvailable}
          selectedBooking={editBookingModal.selectedBooking}
          teeTimeLock={editBookingModal.teeTimeLock}
          refreshTeeTime={true}
          loading={editBookingModal.loading}
          loadingText={editBookingModal.loadingText}
          reloadTeeTimes={props.reloadTeeTimeTabs}
          reloadTeeTime={(updateTeetimeTabAction: TLoadingUpdateBookingAction) =>
            props.reloadActionTeeTimeTabs(updateTeetimeTabAction, true)
          }
        />
      </Portal>

      {/* POPUPS */}
      {/* BLOCKED TEE TIME POPUP */}
      <Portal isMounted={blockedTeeTimePopup.isOpen}>
        <Popup
          open={blockedTeeTimePopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.030")}
          description={t("elements.tee_sheet.tee_sheet_tabs.031")}
          onOk={() => {
            closeBlockedTeeTimePopup();
            void handleInitiateBooking(state.selectedTeeTime);
          }}
          okText={t("elements.tee_sheet.tee_sheet_tabs.032")}
          onCancel={closeBlockedTeeTimePopup}
          backDropCancel={true}
        />
      </Portal>

      {/* CANCEL BOOKING POPUP */}
      <Portal isMounted={cancelBookingPopup.isOpen}>
        <Popup
          open={cancelBookingPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.086")}
          description={`${t("elements.tee_sheet.tee_sheet_tabs.087")} ${cancelBookingPopup?.teetimeToCancelDate} at ${
            cancelBookingPopup?.teetimeToCancelTime
          }?`}
          onOk={cancelBooking}
          okText={t("elements.tee_sheet.tee_sheet_tabs.088")}
          onCancel={() =>
            updateCancelBookingPopup({
              isOpen: false,
              teetimeToCancelDate: "",
              teetimeToCancelTime: "",
              teetimeToCancelBookingId: null,
            })
          }
          backDropCancel={false}
        >
          <div className="mt-3">
            <Checkbox
              id={"send_cancellation_email"}
              size="small"
              value={cancelBookingPopup.send_cancellation_email}
              checked={cancelBookingPopup.send_cancellation_email}
              onChange={handleCheckboxChange}
              label={t("elements.tee_sheet.tee_sheet_tabs.179")}
            />
          </div>
        </Popup>
      </Portal>

      {/* CANCEL PLAYER POPUP */}
      <Portal isMounted={cancelPlayerPopup.isOpen}>
        <Popup
          open={cancelPlayerPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.089")}
          description={`${t("elements.tee_sheet.tee_sheet_tabs.090")} ${cancelPlayerPopup.bookingParticipantName}?`}
          onOk={cancelBookingParticipant}
          okText={t("elements.tee_sheet.tee_sheet_tabs.091")}
          onCancel={() =>
            updateCancelPlayerPopup({
              isOpen: false,
              bookingParticipantName: "",
              bookingParticipantId: null,
            })
          }
          backDropCancel={false}
        />
      </Portal>

      {/* TEE TIME BOOKED POPUP */}
      <Portal isMounted={teeTimeBookedPopup.isOpen}>
        <Popup
          open={teeTimeBookedPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.016")}
          description={t("elements.tee_sheet.tee_sheet_tabs.017")}
          onOk={closeTeeTimeBookedPopup}
          okText={t("elements.tee_sheet.tee_sheet_tabs.032")}
          onCancel={closeTeeTimeBookedPopup}
          backDropCancel={false}
        />
      </Portal>

      {/* TEE TIME LOCKED POPUP */}
      <Portal isMounted={teeTimeLockedPopup.isOpen}>
        <Popup
          open={teeTimeLockedPopup.isOpen}
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.014")}
          description={t("elements.tee_sheet.tee_sheet_tabs.015")}
          onOk={closeTeeTimeLockedPopup}
          okText={t("elements.tee_sheet.tee_sheet_tabs.032")}
          onCancel={closeTeeTimeLockedPopup}
          backDropCancel={true}
        />
      </Portal>

      {/* REMOVE TEE TIME POPUP */}
      <Portal isMounted={removeTeeTimePopup.isOpen}>
        <Popup
          open={removeTeeTimePopup.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.128")}
          description={t("elements.tee_sheet.tee_sheet_tabs.129")}
          onOk={removeTeeTime}
          okText={t("elements.tee_sheet.tee_sheet_tabs.130")}
          onCancel={closeRemoveTeeTimePopup}
          backDropCancel={false}
        />
      </Portal>

      {/* REMOVE SHOTGUN POPUP */}
      <Portal isMounted={removeShotgunPopup.isOpen}>
        <Popup
          open={removeShotgunPopup.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.099")}
          description={t("elements.tee_sheet.tee_sheet_tabs.100")}
          onOk={removeShotgunTemplate}
          okText={t("elements.tee_sheet.tee_sheet_tabs.101")}
          onCancel={closeRemoveShotgunPopup}
          backDropCancel={false}
        />
      </Portal>

      <ImportDrawSheetModal onCancel={closeFileImportModal} onOk={importDrawsheet} open={fileImportModal.isOpen} />

      {/* CHECK IN SHOTGUN PARTICIPANTS */}
      <Portal isMounted={shotgunCheckInParticipants.isOpen}>
        <Popup
          open={shotgunCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.181")}
          description={t("elements.tee_sheet.tee_sheet_tabs.182")}
          onOk={checkInAllShotgunParticipants}
          okText={t("elements.tee_sheet.tee_sheet_tabs.183")}
          onCancel={closeCheckInAllShotgunParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN TOURNAMENT PARTICIPANTS */}
      <Portal isMounted={tournamentCheckInParticipants.isOpen}>
        <Popup
          open={tournamentCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.181")}
          description={t("elements.tee_sheet.tee_sheet_tabs.184")}
          onOk={checkInAllTournamentParticipants}
          okText={t("elements.tee_sheet.tee_sheet_tabs.183")}
          onCancel={closeCheckInAllTournamentParticipants}
          backDropCancel={false}
        />
      </Portal>

      {/* CHECK IN LEAGUE PARTICIPANTS */}
      <Portal isMounted={leagueCheckInParticipants.isOpen}>
        <Popup
          open={leagueCheckInParticipants.isOpen}
          stacked
          type="warning"
          title={t("elements.tee_sheet.tee_sheet_tabs.181")}
          description={t("elements.tee_sheet.tee_sheet_tabs.185")}
          onOk={checkInAllLeagueParticipants}
          okText={t("elements.tee_sheet.tee_sheet_tabs.183")}
          onCancel={closeCheckInAllLeagueParticipants}
          backDropCancel={false}
        />
      </Portal>
    </>
  );
}
